import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useUser } from './UserContext';
import { scriptService, SaveScriptData, ScriptPartData, ScriptSegmentData, CompleteScript, Voice } from '../services/scriptService';
import { SegmentData } from '../../backend/src/types/database';
import { audioMixService, AudioMixData } from '../services/audioMixService';
import { metadataService, ScriptMetadata } from '../services/metadataService';
import { authService } from '../services/auth';
import { config } from '../config';

export interface TextSegmentData {
  id: string; // Frontend ID (segment-1, etc.)
  type: 'text' | 'pause';
  content: string;
  pauseTime?: string;
  isManuallyModified?: boolean;
  isParagraphBreak?: boolean;
  customPrompt?: string | null; // Custom prompt for text segments (null to clear) - DEPRECATED
  speechSpeed?: number; // Speech speed for this segment (overrides global setting)
  inputAudioFile?: string | null; // Input audio file path for voice changer/speech-to-speech
  ttsCacheId?: string | null; // TTS cache ID for generated audio
  // Change tracking for saves (segments stored as JSON in parts now)
  isNew?: boolean; // Whether this is a new segment
  hasChanged?: boolean; // Whether content has changed since last save
  // Speech generation status
  generationStatus?: 'idle' | 'queued' | 'generating' | 'completed' | 'error';
  generatedAudioFile?: string | null; // Generated audio file path
  generationError?: string | null; // Error message if generation failed
}

// Type for content data structure
type ContentData = Record<string, TextSegmentData[]>;

export interface AudioSettings {
  speechSpeed: number;
  commaPause: number;
  periodPause: number;
  colonPause: number;
  dashPause: number;
  paragraphPause: number;
}

// Intelligent Zen Mode types
export interface ZenPartOptions {
  minMinutes: number;        // 0.5 steps
  maxMinutes: number;
  popularOptions: string[];  // keys from ZEN_PART_POPULAR_OPTIONS
  customInstructions: string;
}

export type ZenStage = 'options' | 'script_generated' | 'speech_generated' | 'mix_generated';

export interface ZenSettings {
  enabled: true;
  goal: string;
  ambience: 'auto' | 'none' | 'beach' | 'waterfall' | 'rain' | 'river' | 'other';
  ambienceOther?: string;
  music: 'auto' | 'none' | 'ambient' | 'piano' | 'flute' | 'harp' | 'violin' | 'other';
  musicOther?: string;
  brainwave: 'auto' | 'none' | 'restoration' | 'dream' | 'twilight' | 'relaxation' | 'focus' | 'alertness' | 'learning';
  whiteNoise: 'auto' | 'none' | 'brown' | 'pink' | 'white';
  partOptions: Record<string, ZenPartOptions>;
  stage: ZenStage;
  mixSummary?: string; // AI mixing agent's explanation, shown on the summary page
}

// Meditation Part Interface - centralized definition for all script creation steps
export interface MeditationPart {
  id: string;
  title: string;
  description: string;
  prompt: string;
  defaultWords: string;
  exampleUserPrompt: string;
}

// Meditation Parts Data - single source of truth for all meditation part definitions
export const meditationParts: MeditationPart[] = [
  {
    id: 'introduction',
    title: 'Introduction',
    description: 'Sets the stage for the meditation by welcoming participants, outlining the meditation\'s purpose, and giving practical instructions (e.g., position, posture, silence devices, close eyes).',
    prompt: 'You will be creating part of a meditation script. This will only be the Introduction part of the meditation. Output should be about {words} words.\n\nInstructions:\n{userPrompt}\n\nFor context, this is the previous part of the script:\n{previousPart}',
    defaultWords: '300',
    exampleUserPrompt: 'Invite the listener to get comfortable either sitting or laying down and close their eyes.  The purpose of this meditation will be being grateful.  Get the listeners mind and body ready for meditation.'
  },
  {
    id: 'relaxation',
    title: 'Relaxation',
    description: 'Guides participants to physically and mentally relax, often using body scans, progressive muscle relaxation, or focus on the breath.',
    prompt: 'You will be creating part of a meditation script. This will only be the Relaxation part of the meditation. Output should be about {words} words.\n\nInstructions:\n{userPrompt}\n\nFor context, this is the previous part of the script:\n{previousPart}',
    defaultWords: '500',
    exampleUserPrompt: 'Invite the listener to relax their mind and body. Have the listener bring their attention to, and then relax and release tension from each part of their body, starting at their head, and going to their toes. After the body scan, have them focus on their breath, and sensations related to breathing.'
  },
  {
    id: 'focus',
    title: 'Focus/Attention',
    description: 'Centers the mind, encouraging sustained attention on the breath, a sound, or a specific point of concentration to foster presence and mindfulness.',
    prompt: 'You will be creating part of a meditation script. This will only be the Focus/Attention part of the meditation. Output should be about {words} words.\n\nInstructions:\n{userPrompt}\n\nFor context, this is the previous part of the script:\n{previousPart}',
    defaultWords: '400',
    exampleUserPrompt: 'Guide the listener to focus their attention on their breath. Have them breathe normally, and notice the sensations related to breathing. (Stomach & chest rising, noticing air moving in and out of the nose, etc.)'
  },
  {
    id: 'visualization',
    title: 'Visualization/Imagery',
    description: 'Leads participants to imagine calming scenes, sensory rich images, or guided stories, engaging the imagination to evoke emotional or physical peace and deepen relaxation.',
    prompt: 'You will be creating part of a meditation script. This will only be the Visualization/Imagery part of the meditation. Output should be about {words} words.\n\nInstructions:\n{userPrompt}\n\nFor context, this is the previous part of the script:\n{previousPart}',
    defaultWords: '600',
    exampleUserPrompt: 'Invite the listener deepen their meditation with imagination and visualization.  Visualize canoeing down a gentle river on a warm spring day.  Describe a peaceful, effortless and relaxing trip where they completely safe, secure. Emphasize sensory details, what can the listener see, hear, smell, feel, and possibly taste.  '
  },
  {
    id: 'selfguided',
    title: 'Self Guided / No Speaking',
    description: 'Offers a period of silence for personal exploration or to let thoughts and feelings arise without verbal guidance, allowing the meditator to direct their own experience.',
    prompt: 'You will be creating part of a meditation script. This will only be the Self Guided / No Speaking part of the meditation. Output should be about {words} words.\n\nInstructions:\n{userPrompt}\n\nFor context, this is the previous part of the script:\n{previousPart}',
    defaultWords: '100',
    exampleUserPrompt: 'Transition the listener into a period of silence for personal reflection with the intention of being grateful. Encourage them to simply be present with whatever arises.'
  },
  {
    id: 'manifestation',
    title: 'Manifestation/Behavior',
    description: 'Encourages envisioning desired reality, goals, and behavior changes as already achieved, using the mind\'s creative powers to set intentions and attract positive outcomes.',
    prompt: 'You will be creating part of a meditation script. This will only be the Manifestation/Behavior part of the meditation. Output should be about {words} words.\n\nInstructions:\n{userPrompt}\n\nFor context, this is the previous part of the script:\n{previousPart}',
    defaultWords: '450',
    exampleUserPrompt: 'Guide the listener to envision their desires or goals as already achieved. Help them use their imagination to create vivid mental images of their ideal life or specific outcomes. Encourage them to feel the emotions associated with achieving these goals.'
  },
  {
    id: 'affirmations',
    title: 'Affirmations/Mantras',
    description: 'Integrates positive phrases, statements, or sacred sounds, either spoken aloud or silently, to reinforce desired mental states and self-beliefs.',
    prompt: 'You will be creating part of a meditation script. This will only be the Affirmations/Mantras part of the meditation. Output should be about {words} words.\n\nInstructions:\n{userPrompt}\n\nFor context, this is the previous part of the script:\n{previousPart}',
    defaultWords: '350',
    exampleUserPrompt: 'Introduce positive affirmations, I am grateful for my life, I am grateful for my health, I am grateful for my family, I am grateful for my friends, I am grateful for love, I am grateful for what I have.  Guide them to repeat these phrases either in their mind or out loud with intention and feeling.'
  },
  {
    id: 'remembrance',
    title: 'Remembrance/Discovery',
    description: 'Guides the listener to remember and discover their true nature, their inherent wisdom, and their connection to the universe.',
    prompt: 'You will be creating part of a meditation script. This will only be the Remembrance/Discovery part of the meditation. Output should be about {words} words.\n\nInstructions:\n{userPrompt}\n\nFor context, this is the previous part of the script:\n{previousPart}',
    defaultWords: '500',
    exampleUserPrompt: 'Guide the listener to remember and discover their true nature, their inherent wisdom, and their connection to the universe.'
  },
  {
    id: 'healing',
    title: 'Healing',
    description: 'Focuses on the body or mind\'s capacity to heal, guiding attention to areas in need of care, sending compassion, or inviting restorative energy.',
    prompt: 'You will be creating part of a meditation script. This will only be the Healing part of the meditation. Output should be about {words} words.\n\nInstructions:\n{userPrompt}\n\nFor context, this is the previous part of the script:\n{previousPart}',
    defaultWords: '500',
    exampleUserPrompt: 'Guide the listener to focus on healing energy within their body and mind. Help them send compassion and healing intention to areas that need care. This could include physical healing, emotional healing, or mental wellness. Encourage them to visualize healing light or energy.'
  },
  {
    id: 'suggestions',
    title: 'Suggestions',
    description: 'Offers gentle, supportive prompts or ideas meant to instill lasting change in thinking, feeling, or behaving (e.g., "You will continue to feel calm and relaxed" or "You will find it easier to forgive yourself and others").',
    prompt: 'You will be creating part of a meditation script. This will only be the Suggestions part of the meditation. Output should be about {words} words.\n\nInstructions:\n{userPrompt}\n\nFor context, this is the previous part of the script:\n{previousPart}',
    defaultWords: '300',
    exampleUserPrompt: 'Guide the listener to notice what it feels like to be extremely relaxed.  Suggest that they can return to this state of mind at any time by mentally spelling and visualizing the word "relax".'
  },
  {
    id: 'awakening',
    title: 'Awakening',
    description: 'Gradually brings awareness back to the room and the present moment, gently instructing participants to breath normally, move their bodies, and prepare to end the meditation.',
    prompt: 'You will be creating part of a meditation script. This will only be the Awakening part of the meditation. Output should be about {words} words.\n\nInstructions:\n{userPrompt}\n\nFor context, this is the previous part of the script:\n{previousPart}',
    defaultWords: '250',
    exampleUserPrompt: 'Gradually guide the listener back to full awareness. Have them slowly return their attention to their breath, gently move their fingers and toes, and prepare to open their eyes. Make the transition gentle and gradual so they feel refreshed and alert.'
  },
  {
    id: 'reflection',
    title: 'Reflection',
    description: 'Invites contemplation on the experience—what arose, what was learned, or how to integrate insights into daily life—often finished with a sense of gratitude or intention for moving forward.',
    prompt: 'You will be creating part of a meditation script. This will only be the Reflection part of the meditation. Output should be about {words} words.\n\nInstructions:\n{userPrompt}\n\nFor context, this is the previous part of the script:\n{previousPart}',
    defaultWords: '200',
    exampleUserPrompt: 'Invite the listener to reflect on their meditation experience. Guide them to consider what arose during the practice, what they learned, and how they can integrate any insights into their daily life. End with gratitude and a positive intention for moving forward.'
  }
];

// Helper function to get simplified parts (only id and title) for components that don't need full data
export const getSimplifiedMeditationParts = () => 
  meditationParts.map(({ id, title }) => ({ id, title }));

interface ScriptCreationState {
  // Script metadata
  scriptId?: string;
  scriptName: string;
  isEditingExisting: boolean;
  
  // Part selection
  selectedParts: string[];
  customPartNames: Record<string, string>;
  collapsedParts: Set<string>;
  
  // Content data
  contentData: Record<string, TextSegmentData[]>;
  editingContent: Record<string, string>;
  
  // Voice & Audio settings
  availableVoices: Voice[];
  selectedVoice: string;
  selectedVoiceData: Voice | null;
  selectedLanguage: string;
  segmentationMode: 'punctuation' | 'sentence' | 'paragraph';
  advancedSettings: AudioSettings;
  
  // UI state
  openMenuSegmentId: string | null;
  dragState: {
    isDragging: boolean;
    partId: string;
    segmentId: string;
    startX: number;
    startValue: number;
  } | null;
  
  // Save state
  isScriptSaved: boolean;
  draftCount: number;
  
  // Workflow control
  hasAccessedSpeech: boolean;
  isWriteReadOnly: boolean;
  shouldOpenToSpeech: boolean;
  shouldOpenToAudio: boolean;
  shouldOpenToPublish: boolean;

  // Intelligent Zen Mode
  zenSettings: ZenSettings | null;
  shouldOpenToZenOptions: boolean;
  shouldOpenToZenPreview: boolean;
  shouldOpenToZenSummary: boolean;
  
  // Database tracking for incremental saves
  scriptDbId?: string; // Actual database script ID
  partDbIds: Record<string, string>; // partId -> database part ID mapping
  segmentDbIds: Record<string, string>; // frontend segment ID -> database segment ID
  lastSavedContent: Record<string, string>; // Last saved content for change detection
  
  // Speech generation state
  generationQueue: string[]; // Array of segment IDs in generation queue
  isGenerating: boolean; // Whether any generation is in progress
  
  // Audio playback state
  isPlayingAudio: boolean; // Whether audio is currently playing
  currentPlayingPart: string | null; // Which part is currently playing
  currentPlayingSegment: string | null; // Which segment is currently playing
  
  // Audio finalization state
  isFinalizingAudio: boolean; // Whether script audio is being finalized
  finalizedAudioFile: string | null; // Path to finalized audio file
  finalizationError: string | null; // Error message if finalization failed
  
  // Script audio file from database
  scriptAudioFile: string | null; // Path to script's main audio file
  scriptFinalAudioFile: string | null; // Path to script's final mixed audio file
  scriptArtFile: string | null; // Path to script's art file
  
  // Publish form data
  publishTitle: string; // Title for publishing (separate from scriptName)
  publishDescription: string; // Description for publishing
  publishImagePrompt: string; // Image generation prompt
  publishTags: string[]; // Selected tags for publishing
  isPrivate: boolean; // Whether to keep meditation private
  autoLoop: boolean; // Whether to automatically loop the meditation
  isAnonymous: boolean; // Whether to publish without username
  isProtected: boolean; // Whether to prevent others from customizing this meditation
  
  // Audio mix state
  audioMixData: AudioMixData | null; // Current audio mix configuration
  isAudioMixSaved: boolean; // Whether the current mix has been saved
  audioMixError: string | null; // Error message from mix operations
  
  // Script metadata state
  scriptMetadata: ScriptMetadata | null; // Generated metadata for publish page
  isGeneratingMetadata: boolean; // Whether metadata is being generated
  metadataError: string | null; // Error message from metadata generation
  scriptData: CompleteScript | null; // Full script data for metadata generation
}

interface ScriptCreationContextType extends ScriptCreationState {
  // Setters
  setScriptName: (name: string) => void;
  setSelectedParts: (parts: string[]) => void;
  setCustomPartNames: (names: Record<string, string>) => void;
  setCollapsedParts: (parts: Set<string>) => void;
  setContentData: (data: Record<string, TextSegmentData[]>) => void;
  setEditingContent: (content: Record<string, string>) => void;
  setAvailableVoices: (voices: Voice[]) => void;
  setSelectedVoice: (voiceId: string) => void;
  setSelectedLanguage: (language: string) => void;
  setSegmentationMode: (mode: 'punctuation' | 'sentence' | 'paragraph') => void;
  setAdvancedSettings: (settings: AudioSettings) => void;
  setOpenMenuSegmentId: (id: string | null) => void;
  setDragState: (state: any) => void;
  setIsScriptSaved: (saved: boolean) => void;
  setScriptDbId: (id: string | undefined) => void;
  setPartDbIds: (ids: Record<string, string>) => void;
  setSegmentDbIds: (ids: Record<string, string>) => void;
  setLastSavedContent: (content: Record<string, string>) => void;
  setHasAccessedSpeech: (accessed: boolean) => void;
  setIsWriteReadOnly: (readOnly: boolean) => void;
  setShouldOpenToSpeech: (shouldOpen: boolean) => void;
  setShouldOpenToAudio: (shouldOpen: boolean) => void;
  setShouldOpenToPublish: (shouldOpen: boolean) => void;
  setZenSettings: (settings: ZenSettings | null) => void;
  setShouldOpenToZenOptions: (shouldOpen: boolean) => void;
  setShouldOpenToZenPreview: (shouldOpen: boolean) => void;
  setShouldOpenToZenSummary: (shouldOpen: boolean) => void;
  setGenerationQueue: (queue: string[]) => void;
  setIsGenerating: (generating: boolean) => void;
  setScriptAudioFile: (audioFile: string | null) => void;
  setScriptFinalAudioFile: (audioFile: string | null) => void;
  setScriptArtFile: (artFile: string | null) => void;
  setPublishTitle: (title: string) => void;
  setPublishDescription: (description: string) => void;
  setPublishImagePrompt: (prompt: string) => void;
  setPublishTags: (tags: string[]) => void;
  setIsPrivate: (isPrivate: boolean) => void;
  setAutoLoop: (autoLoop: boolean) => void;
  setIsAnonymous: (isAnonymous: boolean) => void;
  setIsProtected: (isProtected: boolean) => void;
  setAudioMixData: (data: AudioMixData | null) => void;
  setIsAudioMixSaved: (saved: boolean) => void;
  setAudioMixError: (error: string | null) => void;
  setScriptMetadata: (metadata: ScriptMetadata | null) => void;
  setIsGeneratingMetadata: (generating: boolean) => void;
  setMetadataError: (error: string | null) => void;
  setScriptData: (data: CompleteScript | null) => void;
  
  // Actions
  saveScript: () => Promise<boolean | undefined>;
  savePublishData: () => Promise<void>;
  saveSegmentsAndUpdatePartText: (partId: string, updatedSegments: TextSegmentData[]) => Promise<void>;
  loadExistingScript: () => Promise<void>;
  loadVoices: () => Promise<void>;
  handleVoiceChange: (voiceId: string) => void;
  playSampleAudio: (voiceId: string) => void;
  togglePart: (partId: string) => void;
  togglePartCollapse: (partId: string) => void;
  handlePartNameChange: (partId: string, newName: string) => void;
  getPartDisplayName: (partId: string, defaultTitle: string) => string;
  movePartUp: (partId: string) => void;
  movePartDown: (partId: string) => void;
  markContentAsChanged: (partId: string) => void;
  generateSegmentSpeech: (partId: string, segmentId: string) => Promise<void>;
  generatePartSpeech: (partId: string) => Promise<void>;
  markSegmentCompleted: (partId: string, segmentId: string, audioFile: string, ttsCacheId?: string) => void;
  markSegmentError: (partId: string, segmentId: string, error: string) => void;
  resetAllSegmentsGenerationStatus: () => void;
  playPartAudio: (partId: string) => Promise<void>;
  playPartAudioFromSegment: (partId: string, startSegmentId: string) => Promise<void>;
  stopPartAudio: () => void;
  finalizeScriptAudio: () => Promise<string | null>;
  
  // Audio mix actions
  saveAudioMix: (mixData: AudioMixData) => Promise<void>;
  loadAudioMix: () => Promise<void>;
  deleteAudioMix: () => Promise<void>;
  setCurrentMixState: (state: any) => void;
  
  // Metadata actions
  generateScriptMetadata: () => Promise<void>;
  generateUnguidedMetadata: (tracks: any[]) => Promise<void>;
  
  // Utility functions
  segmentsToText: (segments: TextSegmentData[]) => string;
}

const ScriptCreationContext = createContext<ScriptCreationContextType | undefined>(undefined);

export const ScriptCreationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Essential debugging only
  useEffect(() => {
    return () => {
      console.log('🔴 ScriptCreationProvider UNMOUNTING');
    };
  }, []);
  
  const { user, isLoggedIn } = useUser();
  const { scriptId } = useParams<{ scriptId?: string }>();
  const location = useLocation();
  
  // State
  const [scriptName, setScriptName] = useState<string>('');
  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [selectedParts, setSelectedParts] = useState<string[]>([]);
  const [customPartNames, setCustomPartNames] = useState<Record<string, string>>({});
  const [collapsedParts, setCollapsedParts] = useState<Set<string>>(new Set());
  const [contentData, setContentData] = useState<Record<string, TextSegmentData[]>>({});
  
  // Debug contentData changes
  useEffect(() => {
    console.log('contentData changed:', Object.keys(contentData).length, 'parts:', Object.keys(contentData));
  }, [contentData]);

  const [editingContent, setEditingContent] = useState<Record<string, string>>({});
  const [selectedVoice, setSelectedVoice] = useState('default');
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [segmentationMode, setSegmentationMode] = useState<'punctuation' | 'sentence' | 'paragraph'>('sentence');
  const [advancedSettings, setAdvancedSettings] = useState<AudioSettings>({
    speechSpeed: 1.0,
    commaPause: 0.3,
    periodPause: 0.7,
    colonPause: 0.6,
    dashPause: 0.3,
    paragraphPause: 2.0
  });
  const [openMenuSegmentId, setOpenMenuSegmentId] = useState<string | null>(null);
  const [dragState, setDragState] = useState<any>(null);
  const [isScriptSaved, setIsScriptSaved] = useState(false);
  const [draftCount, setDraftCount] = useState(0);
  const [availableVoices, setAvailableVoices] = useState<Voice[]>([]);
  const isLoadingVoicesRef = useRef<boolean>(false);
  const [selectedVoiceData, setSelectedVoiceData] = useState<Voice | null>(null);
  
  // Workflow control state
  const [hasAccessedSpeech, setHasAccessedSpeech] = useState(false);
  const [isWriteReadOnly, setIsWriteReadOnly] = useState(false);
  const [shouldOpenToSpeech, setShouldOpenToSpeech] = useState(false);
  const [shouldOpenToAudio, setShouldOpenToAudio] = useState(false);
  const [shouldOpenToPublish, setShouldOpenToPublish] = useState(false);

  // Intelligent Zen Mode
  const [zenSettings, setZenSettings] = useState<ZenSettings | null>(null);
  const [shouldOpenToZenOptions, setShouldOpenToZenOptions] = useState(false);
  const [shouldOpenToZenPreview, setShouldOpenToZenPreview] = useState(false);
  const [shouldOpenToZenSummary, setShouldOpenToZenSummary] = useState(false);
  
  // Database tracking state
  const [scriptDbId, setScriptDbId] = useState<string | undefined>(undefined);
  const [partDbIds, setPartDbIds] = useState<Record<string, string>>({});
  const [deletedPartDbIds, setDeletedPartDbIds] = useState<string[]>([]); // Track parts to delete from DB
  const [segmentDbIds, setSegmentDbIds] = useState<Record<string, string>>({});
  const [lastSavedContent, setLastSavedContent] = useState<Record<string, string>>({});
  
  // Speech generation state
  const [generationQueue, setGenerationQueue] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  
  // Audio playback state
  const [isPlayingAudio, setIsPlayingAudio] = useState<boolean>(false);
  const [currentPlayingPart, setCurrentPlayingPart] = useState<string | null>(null);
  const [currentPlayingSegment, setCurrentPlayingSegment] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isPlayingRef = useRef<boolean>(false);
  const currentPlaybackId = useRef<string | null>(null);

  // Audio finalization state
  const [isFinalizingAudio, setIsFinalizingAudio] = useState<boolean>(false);
  const [finalizedAudioFile, setFinalizedAudioFile] = useState<string | null>(null);
  const [finalizationError, setFinalizationError] = useState<string | null>(null);
  
  // Script audio file from database
  const [scriptAudioFile, setScriptAudioFile] = useState<string | null>(null);
  const [scriptFinalAudioFile, setScriptFinalAudioFile] = useState<string | null>(null);
  const [scriptArtFile, setScriptArtFile] = useState<string | null>(null);
  
  // Publish form data
  const [publishTitle, setPublishTitle] = useState<string>('');
  const [publishDescription, setPublishDescription] = useState<string>('');
  const [publishImagePrompt, setPublishImagePrompt] = useState<string>('');
  const [publishTags, setPublishTags] = useState<string[]>([]);
  const [isPrivate, setIsPrivate] = useState<boolean>(false);
  const [autoLoop, setAutoLoop] = useState<boolean>(false);
  const [isAnonymous, setIsAnonymous] = useState<boolean>(false);
  const [isProtected, setIsProtected] = useState<boolean>(false);
  
  // Audio mix state
  const [audioMixData, setAudioMixData] = useState<AudioMixData | null>(null);
  const [isAudioMixSaved, setIsAudioMixSaved] = useState<boolean>(true);
  const [audioMixError, setAudioMixError] = useState<string | null>(null);
  const [currentMixState, setCurrentMixState] = useState<any>(null); // Store current mix state
  
  // Script metadata state
  const [scriptMetadata, setScriptMetadata] = useState<ScriptMetadata | null>(null);
  const [isGeneratingMetadata, setIsGeneratingMetadata] = useState<boolean>(false);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [scriptData, setScriptData] = useState<CompleteScript | null>(null);

  // Helper function to convert segments back to text (from NewScript.tsx)
  const segmentsToText = (segments: TextSegmentData[]): string => {
    let text = '';
    
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const prevSegment = i > 0 ? segments[i - 1] : null;
      const nextSegment = i < segments.length - 1 ? segments[i + 1] : null;
      
      if (segment.type === 'text') {
        text += segment.content;
      } else if (segment.type === 'pause') {
        if (segment.content === '\n' && !segment.isManuallyModified) {
          text += '\n\n';
        } else if (segment.isManuallyModified && (segment.content === '\n' || segment.content.match(/^\[(\d*\.?\d+)([sm])?\]$/))) {
          const isParagraphBreak = segment.isParagraphBreak || segment.content === '\n';
          
          if (isParagraphBreak) {
            let formattedPause;
            if (segment.content.match(/^\[(\d*\.?\d+)([sm])?\]$/)) {
              formattedPause = segment.content;
            } else {
              const pauseValue = parseFloat(segment.pauseTime?.replace(/[^\d.]/g, '') || '0');
              if (pauseValue >= 60 && pauseValue % 60 === 0) {
                formattedPause = `[${pauseValue / 60}m]`;
              } else if (pauseValue >= 60) {
                formattedPause = `[${Math.round((pauseValue / 60) * 10) / 10}m]`;
              } else {
                formattedPause = `[${pauseValue}s]`;
              }
            }
            text += `\n\n${formattedPause}\n\n`;
          } else {
            const needsSpaceBefore = prevSegment && prevSegment.type === 'text' && 
              !prevSegment.content.endsWith(' ') && !prevSegment.content.endsWith('\n');
            const needsSpaceAfter = nextSegment && nextSegment.type === 'text' && 
              !nextSegment.content.startsWith(' ') && !nextSegment.content.startsWith('\n');
            
            if (needsSpaceBefore) text += ' ';
            text += segment.content;
            if (needsSpaceAfter) text += ' ';
          }
        } else if (segment.isManuallyModified && segment.content.match(/^\[(\d*\.?\d+)([sm])?\]$/)) {
          const needsSpaceBefore = prevSegment && prevSegment.type === 'text' && 
            !prevSegment.content.endsWith(' ') && !prevSegment.content.endsWith('\n');
          const needsSpaceAfter = nextSegment && nextSegment.type === 'text' && 
            !nextSegment.content.startsWith(' ') && !nextSegment.content.startsWith('\n');
          
          if (needsSpaceBefore) text += ' ';
          text += segment.content;
          if (needsSpaceAfter) text += ' ';
        } else if (segment.isManuallyModified) {
          let formattedPause;
          if (segment.content && segment.content.match(/^\[(\d*\.?\d+)([sm])?\]$/)) {
            formattedPause = segment.content;
          } else {
            const pauseValue = parseFloat(segment.pauseTime?.replace(/[^\d.]/g, '') || '0');
            if (pauseValue >= 60 && pauseValue % 60 === 0) {
              formattedPause = `[${pauseValue / 60}m]`;
            } else if (pauseValue >= 60) {
              formattedPause = `[${Math.round((pauseValue / 60) * 10) / 10}m]`;
            } else {
              formattedPause = `[${pauseValue}s]`;
            }
          }
          
          const needsSpaceBefore = prevSegment && prevSegment.type === 'text' && 
            !prevSegment.content.endsWith(' ') && !prevSegment.content.endsWith('\n');
          const needsSpaceAfter = nextSegment && nextSegment.type === 'text' && 
            !nextSegment.content.startsWith(' ') && !nextSegment.content.startsWith('\n');
          
          if (needsSpaceBefore) text += ' ';
          text += formattedPause;
          if (needsSpaceAfter) text += ' ';
        } else {
          const needsSpaceAfter = nextSegment && nextSegment.type === 'text' && 
            !nextSegment.content.startsWith(' ') && !nextSegment.content.startsWith('\n');
          
          text += segment.content;
          if (needsSpaceAfter) text += ' ';
        }
      }
    }
    
    return text;
  };

  // Track previous content data to detect real content changes
  const prevContentDataRef = useRef<ContentData>({});
  
  // Track page navigation for debugging
  useEffect(() => {
    console.log('NAVIGATION: Changed to', location.pathname);
  }, [location.pathname]);
  
  // Sync editingContent from contentData when we have segments (for Speech -> Write transitions)
  useEffect(() => {
    let hasUpdates = false;
    const updates: Record<string, string> = {};
    
    selectedParts.forEach(partId => {
      const hasSegments = contentData[partId] && contentData[partId].length > 0;
      
      // Check if content actually changed (not just generation status)
      const prevSegments = prevContentDataRef.current[partId] || [];
      const currentSegments = contentData[partId] || [];
      
      // Compare actual content, not generation status
      const contentChanged = currentSegments.length !== prevSegments.length ||
        currentSegments.some((seg, idx) => {
          const prevSeg = prevSegments[idx];
          if (!prevSeg) return true;
          return seg.content !== prevSeg.content || seg.type !== prevSeg.type || 
                 (seg.type === 'pause' && seg.pauseTime !== prevSeg.pauseTime);
        });
      
      // Only reconstruct if content actually changed
      if (hasSegments && contentChanged) {
        console.log(`Context sync: Content changed for ${partId}, reconstructing`);
        const reconstructedText = segmentsToText(contentData[partId]);
        updates[partId] = reconstructedText;
        hasUpdates = true;
      }
    });
    
    // Update ref for next comparison
    prevContentDataRef.current = contentData;
    
    if (hasUpdates) {
      console.log('Context: Syncing editingContent from contentData');
      setEditingContent(prevEditingContent => {
        const newEditingContent = { ...prevEditingContent };
        Object.entries(updates).forEach(([partId, text]) => {
          if (newEditingContent[partId] !== text) {
            console.log(`Context: Text differs, updating editingContent for ${partId}`);
            newEditingContent[partId] = text;
          }
        });
        return newEditingContent;
      });
    }
  }, [contentData, selectedParts]); // Only depend on contentData and selectedParts to avoid loops

  // Initialize script name when user is available
  useEffect(() => {
    if (user && !scriptName) {
      const defaultName = `${user.username || user.name || 'Your'}'s meditation`;
      setScriptName(defaultName);
    }
  }, [user, scriptName]);

  // Track previous scriptId and scriptDbId to detect route transitions
  const prevScriptIdRef = useRef<string | undefined>(scriptId);
  const prevScriptDbIdRef = useRef<string | undefined>(scriptDbId);
  const hasLoadedScriptRef = useRef<boolean>(false);
  
  // Reset state when transitioning from edit to create (New Script)
  useEffect(() => {
    const prevScriptId = prevScriptIdRef.current;
    const prevScriptDbId = prevScriptDbIdRef.current;
    
    // Reset the loaded flag if script ID changes
    if (prevScriptId !== scriptId) {
      hasLoadedScriptRef.current = false;
    }
    const currentPathname = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    
    // Detect transitions that should reset the context:
    // 1. Going from /edit/:id to /create (URL-based editing to new script)
    // 2. "New Script" was clicked (has ?new=true query parameter)
    const isEditToCreate = prevScriptId && !scriptId && isEditingExisting;
    const isNewScriptClicked = searchParams.get('new') === 'true' && 
                               (currentPathname === '/create' || currentPathname === '/create/plan') &&
                               (prevScriptDbId || isEditingExisting); // Only reset if we had existing work
    
    console.log('🔍 Reset conditions:', {
      isEditToCreate,
      isNewScriptClicked,
      prevScriptId,
      scriptId,
      isEditingExisting,
      newParam: searchParams.get('new'),
      currentPathname,
      prevScriptDbId
    });
    
    if (isEditToCreate || isNewScriptClicked) {
      console.log('🚨 RESETTING CONTENTDATA - This should not happen during normal navigation!');
      
      // Reset all state to initial values
      const defaultName = user ? `${user.username || user.name || 'Your'}'s meditation` : '';
      setScriptName(defaultName);
      setIsEditingExisting(false);
      setSelectedParts([]);
      setCustomPartNames({});
      setCollapsedParts(new Set());
      setContentData({});
      setEditingContent({});
      setSelectedVoice('default');
      setSelectedLanguage('en');
      setAdvancedSettings({
        speechSpeed: 1.0,
        commaPause: 0.3,
        periodPause: 0.7,
        colonPause: 0.6,
        dashPause: 0.3,
        paragraphPause: 2.0
      });
      setOpenMenuSegmentId(null);
      setDragState(null);
      setIsScriptSaved(false);
      setScriptDbId(undefined);
      setPartDbIds({});
      setSegmentDbIds({});
      setLastSavedContent({});
      setHasAccessedSpeech(false);
      setIsWriteReadOnly(false);
      setShouldOpenToSpeech(false);
      setShouldOpenToAudio(false);
      setShouldOpenToPublish(false);
      setZenSettings(null);
      setShouldOpenToZenOptions(false);
      setShouldOpenToZenPreview(false);
      setShouldOpenToZenSummary(false);
      setScriptAudioFile(null);
      setScriptFinalAudioFile(null);
      setScriptArtFile(null);
      setGenerationQueue([]);
      setIsGenerating(false);
      setIsPlayingAudio(false);
      setCurrentPlayingPart(null);
      setIsFinalizingAudio(false);
      setFinalizedAudioFile(null);
      setFinalizationError(null);
      setPublishTitle('');
      setPublishDescription('');
      setPublishImagePrompt('');
      setPublishTags([]);
      setIsPrivate(false);
      setAutoLoop(false);
      setIsAnonymous(false);
      setAudioMixData(null);
      setIsAudioMixSaved(true);
      setAudioMixError(null);
      setScriptMetadata(null);
      setIsGeneratingMetadata(false);
      setMetadataError(null);
      setScriptData(null);
    }
    
    // Update the refs to track current values for next comparison
    prevScriptIdRef.current = scriptId;
    prevScriptDbIdRef.current = scriptDbId;
  }, [scriptId, scriptDbId, isEditingExisting, user, location.pathname, location.search]);

  // Load existing script if editing
  const loadExistingScript = useCallback(async () => {
    if (!scriptId || !isLoggedIn) return;
    
    // Check if we've already loaded this script
    if (hasLoadedScriptRef.current && prevScriptIdRef.current === scriptId) {
      console.log('loadExistingScript: Script already loaded, skipping reload');
      return;
    }
    
    console.log('loadExistingScript: Loading script', scriptId);
    
    try {
      console.log('Loading script for editing:', scriptId);
      const completeScript: CompleteScript = await scriptService.getScript(scriptId);
      const { script, parts } = completeScript;
      
      console.log('Loaded script:', script);
      console.log('Loaded parts:', parts);
      
      if (!script || !parts) {
        console.log('Script or parts is undefined, returning early');
        return;
      }
      
      // Store complete script data for metadata generation
      setScriptData(completeScript);
      
      // Set script metadata
      setScriptName(script.script_name);
      setIsEditingExisting(true);
      
      // Set database tracking
      setScriptDbId(script.id);
      
      // Set script audio file
      setScriptAudioFile(script.audio_file || null);
      setScriptFinalAudioFile(script.final_audio_file || null);
      setScriptArtFile(script.art_file || null);
      
      // Set publish data from script
      setPublishTitle(script.script_name);
      setPublishDescription(script.script_description || '');
      setPublishImagePrompt(script.art_prompt || '');
      setIsPrivate(script.is_private || false);
      setAutoLoop(script.auto_loop || false);
      setIsAnonymous(script.is_anonymous || false);
      
      // Load tags separately
      try {
        const tags = await scriptService.getScriptTags(script.id);
        if (Array.isArray(tags)) {
          const tagNames = tags.map((tag: any) => tag.tag_name);
          setPublishTags(tagNames);
        } else {
          console.error('Tags response is not an array:', tags);
          setPublishTags([]);
        }
      } catch (error) {
        console.error('Error loading script tags:', error);
        setPublishTags([]);
      }
      
      // Set selected parts based on script data
      setSelectedParts(script.selected_parts || []);
      
      // Set UI state from script
      setCollapsedParts(new Set(script.collapsed_parts || []));
      
      // Set voice settings
      setSelectedVoice(script.voice || 'default');
      setSelectedLanguage(script.speech_language || 'en');
      setSegmentationMode(script.segmentation_mode || 'sentence');
      
      // Prepare loaded advanced settings
      const loadedAdvancedSettings = {
        speechSpeed: script.speech_speed || 1.0,
        commaPause: script.comma_pause || 0.3,
        periodPause: script.period_pause || 0.7,
        colonPause: script.colon_pause || 0.6,
        dashPause: script.dash_pause || 0.3,
        paragraphPause: script.paragraph_pause || 2.0
      };
      
      // Set advanced settings
      setAdvancedSettings(loadedAdvancedSettings);
      
      // Set custom part names and populate database tracking from parts data
      const partNames: Record<string, string> = {};
      const newPartDbIds: Record<string, string> = {};
      const newSegmentDbIds: Record<string, string> = {};
      const contentDataFromParts: Record<string, TextSegmentData[]> = {};
      const editingContentFromParts: Record<string, string> = {};
      const lastSavedContentFromParts: Record<string, string> = {};
      
      // Parse text to recreate segments with database associations
      const parseTextToSegments = (text: string, existingSegments: any[] = [], settingsToUse?: AudioSettings, frontendPartId: string = 'default'): TextSegmentData[] => {
        const segments: TextSegmentData[] = [];
        let currentId = 0;
        let segmentDbIndex = 0; // Track which database segment we're on
        
        // Clean up text by normalizing multiple newlines and whitespace
        // Replace multiple consecutive newlines with double newlines
        // This prevents issues from contenteditable <br> tags creating excessive breaks
        const cleanedText = text
          .replace(/\u00A0/g, ' ')     // Replace non-breaking spaces with regular spaces
          .replace(/\u200B/g, '')      // Remove zero-width spaces
          .replace(/\n{3,}/g, '\n\n')  // Replace 3+ newlines with just 2
          .replace(/[ \t]+\n/g, '\n')  // Remove trailing spaces before newlines
          .replace(/\n[ \t]+/g, '\n')  // Remove leading spaces after newlines
          .replace(/\n\s*\n/g, '\n\n') // Normalize paragraph breaks
          .trim();                     // Remove leading/trailing whitespace
        
        // Split by one or more newlines
        const paragraphs = cleanedText.split(/\n+/);
        
        for (let paragraphIndex = 0; paragraphIndex < paragraphs.length; paragraphIndex++) {
          const paragraph = paragraphs[paragraphIndex].trim();
          
          if (paragraph) {
            // Check if this is a standalone pause notation
            const standalonePauseMatch = paragraph.match(/^\[(\d*\.?\d+)([sm])?\]$/);
            if (standalonePauseMatch) {
              // This is a pause segment - don't associate with database
              const value = parseFloat(standalonePauseMatch[1]);
              const unit = standalonePauseMatch[2] || 's';
              const pauseTime = unit === 'm' ? `${value * 60}s` : `${value}s`;
              segments.push({
                id: `${frontendPartId}-segment-${currentId++}`,
                type: 'pause' as const,
                content: paragraph,
                pauseTime: pauseTime,
                isManuallyModified: true,
                isParagraphBreak: true,
                // dbPartId no longer needed - segments stored as JSON in parts
                isNew: false,
                hasChanged: false
              });
            } else {
              // Split by punctuation marks OR pause notation
              const parts = paragraph.split(/(\s*[.!?,:;-]\s*|\[\d*\.?\d+[sm]?\])/);
              
              for (let i = 0; i < parts.length; i++) {
                const part = parts[i].trim();
                if (!part) continue;
                
                const pauseMatch = part.match(/^\[(\d*\.?\d+)([sm])?\]$/);
                if (pauseMatch) {
                  // Pause segment
                  const value = parseFloat(pauseMatch[1]);
                  const unit = pauseMatch[2] || 's';
                  const pauseTime = unit === 'm' ? `${value * 60}s` : `${value}s`;
                  segments.push({
                    id: `${frontendPartId}-segment-${currentId++}`,
                    type: 'pause' as const,
                    content: part,
                    pauseTime: pauseTime,
                    isManuallyModified: true,
                    isParagraphBreak: false,
                    // dbPartId no longer needed - segments stored as JSON in parts
                    isNew: false,
                    hasChanged: false
                  });
                } else if (/[.!?,:;-]/.test(part)) {
                  // Punctuation pause
                  const punctuationMatch = part.match(/[.!?,:;-]/);
                  const punctuationMark = punctuationMatch ? punctuationMatch[0] : part;
                  const pauseTime = getPauseTimeForPunctuation(punctuationMark, settingsToUse);
                  segments.push({
                    id: `${frontendPartId}-segment-${currentId++}`,
                    type: 'pause' as const,
                    content: punctuationMark,
                    pauseTime: pauseTime,
                    isManuallyModified: false,
                    isParagraphBreak: false,
                    // dbPartId no longer needed - segments stored as JSON in parts
                    isNew: false,
                    hasChanged: false
                  });
                } else {
                  // Text segment - associate with database segment if available
                  const dbSegment = existingSegments[segmentDbIndex];
                  segments.push({
                    id: `${frontendPartId}-segment-${currentId++}`,
                    type: 'text' as const,
                    content: part,
                    customPrompt: dbSegment?.custom_prompt,
                    inputAudioFile: dbSegment?.input_audio_file,
                    // dbSegmentId no longer needed - segments stored as JSON in parts
                    // dbPartId no longer needed - segments stored as JSON in parts
                    isNew: false,
                    hasChanged: false,
                    // Preserve generation status from database
                    generationStatus: dbSegment?.ai_generated ? 'completed' as const : 'idle' as const,
                    generatedAudioFile: dbSegment?.audio_file || null,
                    generationError: null
                  });
                  
                  // Track segment database ID
                  if (dbSegment) {
                    newSegmentDbIds[`segment-${currentId - 1}`] = dbSegment.id;
                    segmentDbIndex++;
                  }
                }
              }
            }
          }
          
          // Add paragraph break between paragraphs
          if (paragraphIndex < paragraphs.length - 1) {
            const hasMoreContent = paragraphs.slice(paragraphIndex + 1).some(p => p.trim().length > 0);
            if (hasMoreContent) {
              segments.push({
                id: `${frontendPartId}-segment-${currentId++}`,
                type: 'pause' as const,
                content: '\n',
                pauseTime: `${(settingsToUse || advancedSettings).paragraphPause}s`,
                isManuallyModified: false,
                isParagraphBreak: true,
                // dbPartId no longer needed - segments stored as JSON in parts
                isNew: false,
                hasChanged: false
              });
            }
          }
        }
        
        // Ensure each part ends with a paragraph pause unless it already does
        if (segments.length > 0) {
          const lastSegment = segments[segments.length - 1];
          
          // Check if the last segment is already a paragraph pause
          // This includes: paragraph breaks (\n), standalone pause notations [2s], or any manually modified pause
          const isLastSegmentParagraphPause = lastSegment.type === 'pause' && 
            (lastSegment.isParagraphBreak || 
             lastSegment.content === '\n' ||
             lastSegment.content.match(/^\[(\d*\.?\d+)([sm])?\]$/) ||
             lastSegment.isManuallyModified);
          
          // If not, add a paragraph pause at the end
          if (!isLastSegmentParagraphPause) {
            segments.push({
              id: `${frontendPartId}-segment-${currentId++}`,
              type: 'pause' as const,
              content: '\n',
              pauseTime: `${(settingsToUse || advancedSettings).paragraphPause}s`,
              isManuallyModified: false,
              isParagraphBreak: true,
              // dbPartId no longer needed - segments stored as JSON in parts
              isNew: false,
              hasChanged: false
            });
            console.log('Added final paragraph pause to end of part');
          } else {
            console.log('Part already ends with paragraph pause');
          }
        }
        
        return segments;
      };

      // Helper function for pause timing (reused from Speech.tsx)
      const getPauseTimeForPunctuation = (punctuation: string, settingsToUse?: AudioSettings): string => {
        const settings = settingsToUse || advancedSettings;
        switch (punctuation) {
          case ',': return `${settings.commaPause}s`;
          case '.': case '!': case '?': return `${settings.periodPause}s`;
          case ':': case ';': return `${settings.colonPause}s`;
          case '-': return `${settings.dashPause}s`;
          case '\n': return `${settings.paragraphPause}s`;
          default: return `${settings.commaPause}s`;
        }
      };
      
      // Process each part to restore state with database associations
      parts.forEach(part => {
        // Store custom part names
        if (part.part_name) {
          partNames[part.part_id] = part.part_name;
        }
        
        // Store part database ID
        newPartDbIds[part.part_id] = part.id;
        
        // Use segments_data as authoritative source if it exists, otherwise parse from text
        if (part.segments_data?.segments && part.segments_data.segments.length > 0) {
          // Convert segments_data to frontend TextSegmentData format
          const segments: TextSegmentData[] = part.segments_data.segments.map((segmentData: SegmentData) => {
            // Calculate generation status for text segments based on audio_file presence
            // generationStatus is transient UI state - always recalculate from audio_file
            const generationStatus: 'idle' | 'queued' | 'generating' | 'completed' | 'error' | undefined = 
              segmentData.type === 'text' 
                ? (segmentData.audio_file ? 'completed' : 'idle')
                : undefined;
            
            return {
              id: segmentData.id,
              type: segmentData.type,
              content: segmentData.content,
              // Use saved pauseTime if available, otherwise calculate from defaults
              pauseTime: segmentData.pauseTime || (segmentData.type === 'pause' ? getPauseTimeForPunctuation(segmentData.content, loadedAdvancedSettings) : undefined),
              // Use saved isManuallyModified if available, otherwise calculate from content
              isManuallyModified: segmentData.isManuallyModified !== undefined ? segmentData.isManuallyModified : (segmentData.type === 'pause' && !segmentData.content.match(/^[.!?,:;\-\n]$/)),
              // Use saved isParagraphBreak if available, otherwise calculate from content
              isParagraphBreak: segmentData.isParagraphBreak !== undefined ? segmentData.isParagraphBreak : (segmentData.content === '\n' || (segmentData.type === 'pause' && !!segmentData.content.match(/^\[(\d*\.?\d+)([sm])?\]$/))),
              customPrompt: (segmentData as any).custom_prompt || null,
              speechSpeed: (segmentData as any).speech_speed || undefined,
              inputAudioFile: segmentData.input_audio_file || null,
              generatedAudioFile: segmentData.audio_file || null,
              ttsCacheId: segmentData.tts_cache_id || null,
              generationStatus: generationStatus,
              isNew: false,
              hasChanged: false
            };
          });
          
          contentDataFromParts[part.part_id] = segments;
          
          // Debug: Log loaded segments with their generation status
          console.log(`Loaded segments for part ${part.part_id}:`, segments.map(s => ({
            id: s.id,
            type: s.type,
            hasAudio: !!s.generatedAudioFile,
            status: s.generationStatus
          })));
          
          // Generate script_part_text from segments for editing
          const generatedText = segments.map(segment => {
            if (segment.type === 'text') {
              return segment.content;
            } else if (segment.type === 'pause') {
              if (segment.content === '\n') return '\n\n'; // Paragraph break
              if (segment.isManuallyModified) return segment.content; // Custom pause notation
              return segment.content; // Punctuation
            }
            return '';
          }).join('');
          
          editingContentFromParts[part.part_id] = generatedText;
          lastSavedContentFromParts[part.part_id] = generatedText;
        } else if (part.script_part_text) {
          // Fallback: Parse text to segments when no segments_data exists
          const segments = parseTextToSegments(part.script_part_text, [], loadedAdvancedSettings, part.part_id);
          contentDataFromParts[part.part_id] = segments;
          editingContentFromParts[part.part_id] = part.script_part_text;
          lastSavedContentFromParts[part.part_id] = part.script_part_text;
        }
      });
      
      // Check if this is an unguided meditation
      const isUnguided = script.is_unguided || (script.selected_parts?.length === 1 && script.selected_parts.includes('selfguided'));

      // Hydrate Intelligent Zen Mode settings (unconditional — clears any
      // zenSettings left over from a previously loaded script)
      const loadedZenSettings: ZenSettings | null = script.zen_settings || null;
      setZenSettings(loadedZenSettings);

      // Check navigation priority: Final audio file > zen stage > Audio file > segments/unguided > write page
      if (script.final_audio_file) {
        console.log('Script has final audio file, should open to Publish page');
        setShouldOpenToPublish(true);
        setHasAccessedSpeech(true);
        setIsWriteReadOnly(true);
      } else if (loadedZenSettings?.stage === 'mix_generated') {
        console.log('Zen script has AI mix, should open to Zen Summary page');
        setShouldOpenToZenSummary(true);
        setHasAccessedSpeech(true);
        setIsWriteReadOnly(true);
      } else if (loadedZenSettings?.stage === 'speech_generated' || loadedZenSettings?.stage === 'script_generated') {
        console.log('Zen script in progress, should open to Zen Preview page');
        setShouldOpenToZenPreview(true);
        setHasAccessedSpeech(true);
        setIsWriteReadOnly(true);
      } else if (script.audio_file) {
        console.log('Script has audio file, should open to Audio page');
        setShouldOpenToAudio(true);
        setHasAccessedSpeech(true);
        setIsWriteReadOnly(true);
      } else if (isUnguided) {
        console.log('Script is unguided, should open to Audio page');
        setShouldOpenToAudio(true);
        setHasAccessedSpeech(true);
        setIsWriteReadOnly(true);
      } else {
        const hasSegments = parts.some(part => part.segments_data?.segments && part.segments_data.segments.length > 0);
        if (hasSegments) {
          console.log('Script has segments, should open to Speech page');
          setShouldOpenToSpeech(true);
          setHasAccessedSpeech(true);
          setIsWriteReadOnly(true);
        } else if (loadedZenSettings) {
          console.log('Zen script without content, should open to Zen Options page');
          setShouldOpenToZenOptions(true);
        }
      }
      
      setCustomPartNames(partNames);
      setPartDbIds(newPartDbIds);
      setSegmentDbIds(newSegmentDbIds);
      
      console.log('loadExistingScript: Setting contentData with', Object.keys(contentDataFromParts).length, 'parts:', Object.keys(contentDataFromParts));
      console.log('loadExistingScript: Current contentData has', Object.keys(contentData).length, 'parts:', Object.keys(contentData));
      
      setContentData(contentDataFromParts);
      setEditingContent(editingContentFromParts);
      setLastSavedContent(lastSavedContentFromParts);
      
      console.log('Script loaded successfully for editing with database tracking');
      console.log('Part DB IDs:', newPartDbIds);
      console.log('Segment DB IDs:', newSegmentDbIds);
      
      // Mark that we've loaded this script
      hasLoadedScriptRef.current = true;
      
    } catch (error) {
      console.error('Error loading script for editing:', error);
    }
  }, [scriptId, isLoggedIn]);

  useEffect(() => {
    console.log('useEffect [scriptId, isLoggedIn] triggered', { scriptId, isLoggedIn });
    
    if (scriptId && isLoggedIn) {
      console.log('Loading script - script ID and logged in detected');
      loadExistingScript();
    } else {
      console.log('Not loading script:', { scriptId, isLoggedIn });
    }
  }, [scriptId, isLoggedIn, loadExistingScript]);

  // Load audio mix data when script database ID becomes available
  useEffect(() => {
    const loadMixData = async () => {
      if (!scriptDbId) return;
      
      try {
        console.log('Auto-loading audio mix data for script:', scriptDbId);
        setAudioMixError(null);
        console.log('Loading audio mix data...');
        
        const mixData = await audioMixService.loadMixData(scriptDbId);
        
        if (mixData) {
          // Validate the loaded data
          if (audioMixService.validateMixData(mixData) && audioMixService.isCompatibleVersion(mixData)) {
            setAudioMixData(mixData);
            setIsAudioMixSaved(true);
            console.log('Audio mix loaded successfully');
          } else {
            console.warn('Invalid or incompatible audio mix data');
            setAudioMixError('Saved audio mix is incompatible with current version');
          }
        } else {
          console.log('No audio mix data found');
          setAudioMixData(null);
          setIsAudioMixSaved(true);
        }
      } catch (error) {
        console.error('Load audio mix error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load audio mix';
        setAudioMixError(errorMessage);
      }
    };

    loadMixData();
  }, [scriptDbId]);

  // Fetch draft count
  useEffect(() => {
    const fetchDraftCount = async () => {
      if (!isLoggedIn) return;
      
      try {
        const userScripts = await scriptService.getUserScripts();
        const drafts = userScripts.filter(script => script.is_draft);
        setDraftCount(drafts.length);
      } catch (error) {
        console.error('Error fetching draft count:', error);
      }
    };

    fetchDraftCount();
  }, [isLoggedIn]);

  // Actions
  const saveScript = async () => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    try {
      setIsScriptSaved(false);
      
      // Check if this is an unguided meditation (only selfguided part selected)
      const isUnguided = selectedParts.length === 1 && selectedParts.includes('selfguided');
      
      // Prepare script metadata
      const scriptData: SaveScriptData = {
        script_name: scriptName,
        script_description: `Meditation script created with ${selectedParts.length} parts`,
        selected_parts: selectedParts,
        show_content: true, // Always true in new workflow
        collapsed_parts: Array.from(collapsedParts),
        voice: selectedVoice,
        speech_language: selectedLanguage,
        speech_speed: advancedSettings.speechSpeed,
        comma_pause: advancedSettings.commaPause,
        period_pause: advancedSettings.periodPause,
        colon_pause: advancedSettings.colonPause,
        dash_pause: advancedSettings.dashPause,
        paragraph_pause: advancedSettings.paragraphPause,
        segmentation_mode: segmentationMode,
        is_unguided: isUnguided,
        // Always included: null explicitly clears zen_settings when the user switches to manual
        zen_settings: zenSettings
      };

      // Prepare parts with ALL segments (including pauses) for saving
      const partsToSave = selectedParts.map((partId, index) => {
        const segments = contentData[partId] || [];
        const partText = editingContent[partId] || segmentsToText(segments);
        
        // Prepare segments_data JSON with ALL segments (text and pause)
        const segmentsData = segments.length > 0 ? {
          segments: segments.map((segment) => ({
            id: segment.id,
            content: segment.content,
            type: segment.type as 'text' | 'pause',
            // Pause-specific fields
            ...(segment.type === 'pause' && {
              pauseTime: segment.pauseTime || undefined,
              isManuallyModified: segment.isManuallyModified || undefined,
              isParagraphBreak: segment.isParagraphBreak || undefined,
            }),
            // Text-specific fields
            ...(segment.type === 'text' && {
              speech_speed: segment.speechSpeed || undefined,
              input_audio_file: segment.inputAudioFile || undefined,
              audio_file: segment.generatedAudioFile || undefined,
              tts_cache_id: segment.ttsCacheId || undefined,
              // generationStatus not saved - transient UI state only
            })
          })),
          lastParsed: new Date().toISOString()
        } : undefined;
        
        if (segmentsData) {
          console.log(`saveScript: Preparing ${segmentsData.segments.length} segments for part ${partId}:`, 
            segmentsData.segments.map(s => ({ id: s.id, type: s.type, content: s.content })));
        }

        const partData: ScriptPartData = {
          part_id: partId,
          part_order: index + 1,
          part_name: customPartNames[partId],
          script_part_text: partText,
          segments_data: segmentsData // Contains ALL segments including pauses
        };

        return {
          partId,
          dbPartId: partDbIds[partId], // Pass the database part ID for updating existing parts
          partData
        };
      });

      let savedScript;
      
      if (isEditingExisting && scriptDbId) {
        // Use incremental saving for existing scripts
        savedScript = await scriptService.saveScriptIncrementally(
          scriptDbId,
          scriptData,
          partsToSave,
          deletedPartDbIds // Pass deleted part IDs for database cleanup
        );

        // Clear deletion queue after successful save
        setDeletedPartDbIds([]);
      } else {
        // For new scripts, create with segments_data
        const partsData = partsToSave.map(({ partData }) => ({
          partData
          // segments_data is already included in partData
        }));
        
        savedScript = await scriptService.saveCompleteScript(
          scriptData,
          partsData
        );
        
        // Update state to indicate we're now editing an existing script
        setIsEditingExisting(true);
      }

      // Update database tracking state
      setScriptDbId(savedScript.script.id);
      console.log('Script saved successfully', savedScript.script.id);
      
      // Store complete script data for metadata generation
      setScriptData(savedScript);
      // Update part database IDs
      const newPartDbIds: Record<string, string> = {};
      
      savedScript.parts.forEach((savedPart, partIndex) => {
        const partId = selectedParts[partIndex];
        newPartDbIds[partId] = savedPart.id;
        
        // Segments are now stored as JSON in parts, no need to track individual segment IDs
      });
      
      setPartDbIds(newPartDbIds);
      // Segment IDs are no longer needed - segments are stored as JSON
      
      // Update content data with database IDs - only if we actually have segments
      const updatedContentData = { ...contentData };
      let hasSegmentsToUpdate = false;
      
      selectedParts.forEach((partId) => {
        if (updatedContentData[partId] && updatedContentData[partId].length > 0) {
          updatedContentData[partId] = updatedContentData[partId].map(segment => ({
            ...segment,
            // Database IDs no longer needed - segments stored as JSON in parts
            isNew: false,
            hasChanged: false
          }));
          hasSegmentsToUpdate = true;
        }
      });
      
      console.log('saveScript: Updating contentData with', Object.keys(updatedContentData).length, 'parts');
      if (hasSegmentsToUpdate) {
        console.log('saveScript: Found segments to update, applying changes');
        setContentData(updatedContentData);
      } else {
        console.log('saveScript: No segments found, preserving existing contentData');
      }
      
      // Update last saved content for change detection
      const newLastSavedContent: Record<string, string> = {};
      selectedParts.forEach(partId => {
        newLastSavedContent[partId] = editingContent[partId] || segmentsToText(contentData[partId] || []);
      });
      setLastSavedContent(newLastSavedContent);
      
      // Save audio mix if it exists (either saved or current unsaved state)
      let mixDataToSave = audioMixData;
      
      // If no saved mix data but we have current mix state, create mix data from current state
      if (!mixDataToSave && currentMixState) {
        mixDataToSave = {
          version: '1.0',
          masterVolume: currentMixState.masterVolume,
          totalDuration: currentMixState.totalDuration || 0,
          tracks: currentMixState.tracks,
          // Preserve existing createdAt if we have audioMixData, otherwise create new timestamp
          createdAt: audioMixData?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      }
      
      if (mixDataToSave) {
        try {
          await audioMixService.saveMixData(savedScript.script.id, mixDataToSave);
          setAudioMixData(mixDataToSave); // Update the saved mix data
          setIsAudioMixSaved(true);
        } catch (mixError) {
          console.error('Error saving audio mix:', mixError);
          // Don't fail the whole save operation, but log the error
          setAudioMixError(mixError instanceof Error ? mixError.message : 'Failed to save audio mix');
        }
      } else {
      }
      
      // Script saved successfully
      setIsScriptSaved(true);

      // Reset saved state after 3 seconds
      setTimeout(() => {
        setIsScriptSaved(false);
      }, 3000);

      return true;
    } catch (error) {
      console.error('Error saving script:', error);
      setIsScriptSaved(false);
      return false;
    }
  };

  // Save publish-specific data
  const savePublishData = async () => {
    if (!scriptDbId) {
      console.error('No script ID available for saving publish data');
      return;
    }

    try {
      // First save the script name if it changed
      await saveScript();
      
      // Then save the publish-specific data
      const response = await fetch(`${config.apiUrl}/scripts/${scriptDbId}/save-publish-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          publishDescription,
          publishImagePrompt,
          publishTags,
          isPrivate,
          autoLoop,
          isAnonymous,
          isProtected
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save publish data');
      }

      const result = await response.json();
      console.log('Publish data saved successfully:', result.message);
      
    } catch (error) {
      console.error('Error saving publish data:', error);
      throw error; // Re-throw so the UI can handle the error
    }
  };

  const togglePart = (partId: string) => {
    // Check current state to determine if adding or removing
    const isRemoving = selectedParts.includes(partId);
    
    console.log(`togglePart: ${isRemoving ? 'Removing' : 'Adding'} part ${partId}`);
    console.log(`togglePart: Current contentData has ${Object.keys(contentData).length} parts:`, Object.keys(contentData));
    
    if (isRemoving) {
      // Removing part - clean up its data
      setSelectedParts(prev => prev.filter(id => id !== partId));
      setContentData(prevContent => {
        const newContent = { ...prevContent };
        delete newContent[partId];
        console.log(`togglePart: After removing ${partId}, contentData will have:`, Object.keys(newContent));
        return newContent;
      });
      setEditingContent(prevEditing => {
        const newEditing = { ...prevEditing };
        delete newEditing[partId];
        return newEditing;
      });
      // Track deletion for database cleanup
      setPartDbIds(prev => {
        const dbPartId = prev[partId];
        if (dbPartId) {
          // Part exists in database, track it for deletion
          setDeletedPartDbIds(prevDeleted => {
            const newDeleted = [...prevDeleted, dbPartId];
            console.log(`togglePart: Added ${partId} (${dbPartId}) to deletion queue. Queue:`, newDeleted);
            return newDeleted;
          });
        }
        const newPartDbIds = { ...prev };
        delete newPartDbIds[partId];
        console.log(`togglePart: Removed ${partId} from partDbIds. Remaining:`, Object.keys(newPartDbIds));
        return newPartDbIds;
      });
    } else {
      // Adding part - initialize its data while preserving existing data
      setSelectedParts(prev => [...prev, partId]);
      setContentData(prevContent => {
        const newContent = {
          ...prevContent,
          [partId]: []
        };
        console.log(`togglePart: After adding ${partId}, contentData will have:`, Object.keys(newContent));
        return newContent;
      });
      setEditingContent(prevEditing => ({
        ...prevEditing,
        [partId]: ''
      }));
    }
  };

  const togglePartCollapse = (partId: string) => {
    setCollapsedParts(prev => {
      const newCollapsed = new Set(prev);
      if (newCollapsed.has(partId)) {
        newCollapsed.delete(partId);
      } else {
        newCollapsed.add(partId);
      }
      return newCollapsed;
    });
  };

  const handlePartNameChange = (partId: string, newName: string) => {
    setCustomPartNames(prev => ({
      ...prev,
      [partId]: newName
    }));
  };

  const getPartDisplayName = (partId: string, defaultTitle: string) => {
    return customPartNames[partId] || defaultTitle;
  };

  const movePartUp = (partId: string) => {
    setSelectedParts(prev => {
      const index = prev.indexOf(partId);
      if (index > 0) {
        const newParts = [...prev];
        [newParts[index - 1], newParts[index]] = [newParts[index], newParts[index - 1]];
        return newParts;
      }
      return prev;
    });
  };

  const movePartDown = (partId: string) => {
    setSelectedParts(prev => {
      const index = prev.indexOf(partId);
      if (index < prev.length - 1) {
        const newParts = [...prev];
        [newParts[index], newParts[index + 1]] = [newParts[index + 1], newParts[index]];
        return newParts;
      }
      return prev;
    });
  };

  const loadVoices = useCallback(async () => {
    console.log('loadVoices called, user:', !!user, 'isLoading:', isLoadingVoicesRef.current);
    
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    // Prevent multiple concurrent loads
    if (isLoadingVoicesRef.current) {
      console.log('Voices already loading, skipping duplicate request');
      return;
    }

    try {
      console.log('Starting voice loading...');
      isLoadingVoicesRef.current = true;
      const voices = await scriptService.getUserVoices();
      setAvailableVoices(voices);
      console.log(`Loaded ${voices.length} voices`);
    } catch (error) {
      console.error('Error loading voices:', error);
    } finally {
      isLoadingVoicesRef.current = false;
      console.log('Voice loading completed');
    }
  }, [user]);

  // Separate effect to update selected voice data when voices or selectedVoice changes
  useEffect(() => {
    if (availableVoices.length > 0 && selectedVoice) {
      const voiceData = availableVoices.find((v: Voice) => v.id === selectedVoice || v.voiceId === selectedVoice) || null;
      setSelectedVoiceData(voiceData);
      console.log(`Selected voice data updated: ${voiceData?.name || 'none'}`);
    }
  }, [availableVoices, selectedVoice]);

  const resetAllSegmentsGenerationStatus = useCallback(() => {
    console.log('Context: Resetting all segments generation status due to voice change');
    
    setContentData(prevData => {
      const updatedData = { ...prevData };
      
      // Reset all text segments in all parts
      Object.keys(updatedData).forEach(partId => {
        const segments = updatedData[partId];
        if (segments) {
          updatedData[partId] = segments.map(segment => {
            if (segment.type === 'text') {
              return {
                ...segment,
                generationStatus: 'idle' as const,
                generatedAudioFile: null,
                generationError: null,
                hasChanged: true // Mark as changed since voice settings changed
                // Preserve: customPrompt, inputAudioFile
              };
            }
            return segment; // Keep pause segments unchanged
          });
        }
      });
      
      return updatedData;
    });
    
    // Clear the completed segments tracking since we're resetting everything
    completedSegmentsRef.current.clear();
  }, []);

  const stopPartAudio = useCallback(() => {
    console.log('Stopping audio playback - clearing all references');
    
    // Invalidate current playback session FIRST
    const oldSessionId = currentPlaybackId.current;
    currentPlaybackId.current = null;
    console.log(`Invalidated session: ${oldSessionId}`);
    
    // Stop current audio element aggressively
    if (audioRef.current) {
      console.log('Stopping audio element');
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        // Remove all event listeners to prevent callbacks
        audioRef.current.onended = null;
        audioRef.current.onloadeddata = null;
        audioRef.current.onerror = null;
        audioRef.current.src = '';
        audioRef.current.load(); // Reset the audio element
      } catch (error) {
        console.log('Error stopping audio:', error);
      }
      audioRef.current = null;
    }
    
    // Clear any pending timeouts
    if (playbackTimeoutRef.current) {
      console.log('Clearing timeout');
      clearTimeout(playbackTimeoutRef.current);
      playbackTimeoutRef.current = null;
    }
    
    // Reset state
    isPlayingRef.current = false;
    setIsPlayingAudio(false);
    setCurrentPlayingPart(null);
    setCurrentPlayingSegment(null);
    console.log('Audio playback stopped - all state reset');
  }, []);

  const playPartAudio = useCallback(async (partId: string) => {
    console.log(`Starting audio playback for part: ${partId}`);
    
    // Stop any existing playback AGGRESSIVELY
    stopPartAudio();
    
    // Give a longer delay to ensure everything stops
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Double-check that we actually stopped everything
    if (audioRef.current) {
      console.log('WARNING: Audio ref still exists after stop, forcing cleanup');
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.src = '';
        audioRef.current.load();
      } catch (error) {
        console.log('Error in forced cleanup:', error);
      }
      audioRef.current = null;
    }
    
    const segments = contentData[partId];
    if (!segments || segments.length === 0) {
      console.error('No segments found for part:', partId);
      return;
    }
    
    // Create unique playback session ID
    const playbackSessionId = `${partId}-${Date.now()}-${Math.random()}`;
    currentPlaybackId.current = playbackSessionId;
    console.log(`Created playback session: ${playbackSessionId}`);
    
    // Set playing state
    isPlayingRef.current = true;
    setIsPlayingAudio(true);
    setCurrentPlayingPart(partId);
    
    try {
      // Play segments sequentially with session ID
      await playSegmentsSequentially(segments, playbackSessionId);
    } catch (error) {
      console.error('Error during audio playback:', error);
    } finally {
      // Only reset state if this is still the current playback session
      if (currentPlaybackId.current === playbackSessionId) {
        console.log(`Playback session ${playbackSessionId} completed normally`);
        isPlayingRef.current = false;
        setIsPlayingAudio(false);
        setCurrentPlayingPart(null);
        currentPlaybackId.current = null;
      } else {
        console.log(`Playback session ${playbackSessionId} was superseded, not resetting state`);
      }
    }
  }, [contentData, stopPartAudio]);

  const playPartAudioFromSegment = useCallback(async (partId: string, startSegmentId: string) => {
    console.log(`Starting audio playback for part: ${partId} from segment: ${startSegmentId}`);
    
    // Stop any existing playback AGGRESSIVELY
    stopPartAudio();
    
    // Give a longer delay to ensure everything stops
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Double-check that we actually stopped everything
    if (audioRef.current) {
      console.log('WARNING: Audio ref still exists after stop, forcing cleanup');
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.src = '';
        audioRef.current.load();
      } catch (error) {
        console.log('Error in forced cleanup:', error);
      }
      audioRef.current = null;
    }
    
    const segments = contentData[partId];
    if (!segments || segments.length === 0) {
      console.error('No segments found for part:', partId);
      return;
    }
    
    // Find the index of the starting segment
    const startIndex = segments.findIndex(s => s.id === startSegmentId);
    if (startIndex === -1) {
      console.error('Start segment not found:', startSegmentId);
      return;
    }
    
    // Get segments from start index onwards
    const segmentsToPlay = segments.slice(startIndex);
    if (segmentsToPlay.length === 0) {
      console.error('No segments to play from start segment:', startSegmentId);
      return;
    }
    
    // Create unique playback session ID
    const playbackSessionId = `${partId}-${Date.now()}-${Math.random()}`;
    currentPlaybackId.current = playbackSessionId;
    console.log(`Created playback session: ${playbackSessionId}`);
    
    // Set playing state
    isPlayingRef.current = true;
    setIsPlayingAudio(true);
    setCurrentPlayingPart(partId);
    
    try {
      // Play segments sequentially with session ID
      await playSegmentsSequentially(segmentsToPlay, playbackSessionId);
    } catch (error) {
      console.error('Error during audio playback from segment:', error);
    } finally {
      // Only reset state if this is still the current playback session
      if (currentPlaybackId.current === playbackSessionId) {
        console.log(`Playback session ${playbackSessionId} completed normally`);
        isPlayingRef.current = false;
        setIsPlayingAudio(false);
        setCurrentPlayingPart(null);
        setCurrentPlayingSegment(null);
        currentPlaybackId.current = null;
      } else {
        console.log(`Playback session ${playbackSessionId} was superseded, not resetting state`);
      }
    }
  }, [contentData, stopPartAudio]);

  const playAudioFile = useCallback((audioFile: string, sessionId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if playback session is still valid before starting
      if (currentPlaybackId.current !== sessionId) {
        console.log(`Audio file cancelled - session ${sessionId} is no longer valid`);
        resolve();
        return;
      }
      
      // Construct full URL if needed (same pattern as in Speech.tsx)
      const audioUrl = audioFile.startsWith('/audio/') 
        ? `${config.apiUrl}${audioFile}`
        : audioFile;
      
      console.log(`Playing audio from: ${audioUrl}`);
      const audio = new Audio(audioUrl);
      
      // Store the audio reference immediately so it can be stopped
      audioRef.current = audio;
      
      audio.onloadeddata = () => {
        // Check again before playing
        if (currentPlaybackId.current !== sessionId) {
          console.log(`Audio cancelled during load - session ${sessionId} is no longer valid`);
          audio.pause();
          audio.currentTime = 0;
          resolve();
          return;
        }
        audio.play().catch((error) => {
          console.log('Audio play failed:', error);
          reject(error);
        });
      };
      
      audio.onended = () => {
        // Check if we're still supposed to be playing this session
        if (currentPlaybackId.current === sessionId) {
          console.log(`Audio segment ended normally for session ${sessionId}`);
          resolve();
        } else {
          console.log(`Audio ended but session ${sessionId} is no longer valid`);
          audio.pause();
          audio.currentTime = 0;
          resolve();
        }
      };
      
      audio.onerror = () => {
        console.log(`Audio error for session ${sessionId}`);
        reject(new Error(`Failed to play audio: ${audioFile}`));
      };
      
      // Start loading the audio
      audio.load();
    });
  }, []);

  const delay = useCallback((ms: number, sessionId: string): Promise<void> => {
    return new Promise((resolve) => {
      // Check if playback session is still valid before starting delay
      if (currentPlaybackId.current !== sessionId) {
        console.log(`Delay cancelled - session ${sessionId} is no longer valid`);
        resolve();
        return;
      }
      
      playbackTimeoutRef.current = setTimeout(() => {
        playbackTimeoutRef.current = null;
        // Check if we're still supposed to be playing this session when delay ends
        if (currentPlaybackId.current === sessionId) {
          resolve();
        } else {
          console.log(`Delay ended but session ${sessionId} is no longer valid`);
        }
      }, ms);
    });
  }, []);

  const parsePauseTime = useCallback((pauseTime: string): number => {
    // Parse pause time string like "2.5s" or "1m" into milliseconds
    const match = pauseTime.match(/^(\d*\.?\d+)([sm]?)$/);
    if (!match) {
      return 500; // Default 0.5 seconds
    }
    
    const value = parseFloat(match[1]);
    const unit = match[2] || 's';
    
    if (unit === 'm') {
      return value * 60 * 1000; // minutes to milliseconds
    } else {
      return value * 1000; // seconds to milliseconds
    }
  }, []);

  const playSegmentsSequentially = useCallback(async (segments: TextSegmentData[], sessionId: string): Promise<void> => {
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      
      // Check if this playback session is still valid
      if (currentPlaybackId.current !== sessionId) {
        console.log(`Playback session ${sessionId} was stopped/superseded, breaking out of loop`);
        break;
      }
      
      if (segment.type === 'text') {
        if (segment.generatedAudioFile) {
          // Set current playing segment before playing
          setCurrentPlayingSegment(segment.id);
          console.log(`Playing audio segment: ${segment.id}`);
          await playAudioFile(segment.generatedAudioFile, sessionId);
          // Clear playing segment after playing
          setCurrentPlayingSegment(null);
        } else {
          // Stop playbook if we encounter a text segment without generated audio
          console.log(`Stopping playback: segment ${segment.id} has no generated audio`);
          console.log('Playback stopped - encountered segment without generated audio. Generate TTS for all segments to play the complete part.');
          break;
        }
      } else if (segment.type === 'pause') {
        // Handle pause segment with silent delay
        const pauseTimeMs = parsePauseTime(segment.pauseTime || '0.5s');
        console.log(`Pausing for ${pauseTimeMs}ms (${segment.pauseTime})`);
        await delay(pauseTimeMs, sessionId);
      }
    }
  }, [playAudioFile, parsePauseTime, delay]);

  const finalizeScriptAudio = useCallback(async (): Promise<string | null> => {
    if (!scriptDbId || !scriptName) {
      console.error('Cannot finalize audio: missing script ID or name');
      setFinalizationError('Cannot finalize audio: script not saved');
      return null;
    }

    try {
      setIsFinalizingAudio(true);
      setFinalizationError(null);
      console.log('Starting script audio finalization...');

      // Save the script first to ensure the selected voice is persisted
      console.log('Saving script before finalization to ensure voice selection is saved...');
      await saveScript();

      // Prepare segments data for all selected parts
      const allSegments: Array<{
        id: string;
        type: 'text' | 'pause';
        content: string;
        audioFile?: string | null;
        pauseTime?: string;
        order: number;
      }> = [];

      let segmentOrder = 0;

      // Process each selected part in order
      for (const partId of selectedParts) {
        const segments = contentData[partId];
        if (!segments) continue;

        for (const segment of segments) {
          allSegments.push({
            id: segment.id,
            type: segment.type,
            content: segment.content,
            audioFile: segment.generatedAudioFile,
            pauseTime: segment.pauseTime,
            order: segmentOrder++
          });
        }
      }

      if (allSegments.length === 0) {
        throw new Error('No segments found to finalize');
      }

      // Check that all text segments have generated audio
      const textSegments = allSegments.filter(s => s.type === 'text');
      const missingAudio = textSegments.filter(s => !s.audioFile);
      
      if (missingAudio.length > 0) {
        throw new Error(`Please generate TTS for all text segments first. ${missingAudio.length} segments are missing audio.`);
      }

      console.log(`Finalizing ${allSegments.length} segments for script: ${scriptName}`);

      // Call the API to finalize the audio
      const result = await scriptService.finalizeScriptAudio(
        scriptDbId,
        scriptName,
        allSegments
      );

      if (result.success) {
        setFinalizedAudioFile(result.audioFile);
        setScriptAudioFile(result.audioFile); // Update script audio file for navigation
        console.log(`Audio finalization completed: ${result.audioFile} (${result.duration.toFixed(1)}s)`);
        return result.audioFile;
      } else {
        throw new Error(result.error || 'Failed to finalize script audio');
      }

    } catch (error) {
      console.error('Audio finalization error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setFinalizationError(errorMessage);
      return null;
    } finally {
      setIsFinalizingAudio(false);
    }
  }, [scriptDbId, scriptName, selectedParts, contentData, saveScript]);

  // Audio mix actions
  const saveAudioMix = useCallback(async (mixData: AudioMixData) => {
    if (!scriptDbId) {
      console.error('Cannot save audio mix: missing script ID');
      setAudioMixError('Cannot save audio mix: script not saved');
      return;
    }

    try {
      setAudioMixError(null);
      console.log('Saving audio mix data...');
      
      await audioMixService.saveMixData(scriptDbId, mixData);
      
      setAudioMixData(mixData);
      setIsAudioMixSaved(true);
      console.log('Audio mix saved successfully');
    } catch (error) {
      console.error('Save audio mix error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save audio mix';
      setAudioMixError(errorMessage);
    }
  }, [scriptDbId]);

  const loadAudioMix = useCallback(async () => {
    if (!scriptDbId) {
      console.log('No script ID available for loading audio mix');
      return;
    }

    try {
      setAudioMixError(null);
      console.log('Loading audio mix data...');
      
      const mixData = await audioMixService.loadMixData(scriptDbId);
      
      if (mixData) {
        // Validate the loaded data
        if (audioMixService.validateMixData(mixData) && audioMixService.isCompatibleVersion(mixData)) {
          setAudioMixData(mixData);
          setIsAudioMixSaved(true);
          console.log('Audio mix loaded successfully, tracks:', mixData.tracks.length);
          console.log('Audio mix data set in context:', mixData);
        } else {
          console.warn('Invalid or incompatible audio mix data');
          setAudioMixError('Saved audio mix is incompatible with current version');
        }
      } else {
        console.log('No audio mix data found');
        setAudioMixData(null);
        setIsAudioMixSaved(true);
      }
    } catch (error) {
      console.error('Load audio mix error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load audio mix';
      setAudioMixError(errorMessage);
    }
  }, [scriptDbId]);

  const deleteAudioMix = useCallback(async () => {
    if (!scriptDbId) {
      console.error('Cannot delete audio mix: missing script ID');
      setAudioMixError('Cannot delete audio mix: script not saved');
      return;
    }

    try {
      setAudioMixError(null);
      console.log('Deleting audio mix data...');
      
      await audioMixService.deleteMixData(scriptDbId);
      
      setAudioMixData(null);
      setIsAudioMixSaved(true);
      console.log('Audio mix deleted successfully');
    } catch (error) {
      console.error('Delete audio mix error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete audio mix';
      setAudioMixError(errorMessage);
    }
  }, [scriptDbId]);

  const generateUnguidedMetadata = useCallback(async (tracks: any[]) => {
    try {
      setIsGeneratingMetadata(true);
      setMetadataError(null);
      console.log('Generating unguided metadata from tracks...');

      // Analyze tracks to extract meaningful information
      const brainwaveTracks = tracks.filter(track => track.type === 'brainwave');
      const musicTracks = tracks.filter(track => track.type === 'music');
      const backgroundTracks = tracks.filter(track => track.type === 'background');
      const soundTracks = tracks.filter(track => track.type === 'sound');

      // Extract brainwave information
      const frequencies = new Set<string>();
      const modulations = new Set<string>();
      
      brainwaveTracks.forEach(track => {
        if (track.brainwaveSettings) {
          // Add modulation type
          modulations.add(track.brainwaveSettings.modulationType);
          
          // Extract frequency ranges from control points
          track.brainwaveSettings.controlPoints?.forEach((point: any) => {
            const brainwaveFreq = point.brainwaveFreq;
            if (brainwaveFreq <= 4) frequencies.add('Delta');
            else if (brainwaveFreq <= 8) frequencies.add('Theta'); 
            else if (brainwaveFreq <= 14) frequencies.add('Alpha');
            else if (brainwaveFreq <= 30) frequencies.add('Beta');
            else frequencies.add('Gamma');
          });
        }
      });

      // Generate title based on what's available
      const freqArray = Array.from(frequencies);
      const hasMultipleFreqs = freqArray.length > 1;
      const primaryFreq = freqArray[0];
      const hasBinaural = modulations.has('binaural');
      
      let title = '';
      if (hasMultipleFreqs) {
        title = `${freqArray.join('/')} Frequency Meditation`;
      } else if (primaryFreq && hasBinaural) {
        title = `${primaryFreq} Binaural Beats Meditation`;
      } else if (primaryFreq && !hasBinaural) {
        title = `${primaryFreq} Brainwave Meditation`;
      } else if (brainwaveTracks.length > 0) {
        // Has brainwave tracks but no clear frequency classification
        title = hasBinaural ? 'Binaural Beats Meditation' : 'Brainwave Meditation';
      } else if (musicTracks.length > 0) {
        title = 'Instrumental Meditation Music';
      } else if (backgroundTracks.length > 0 && soundTracks.length > 0) {
        title = 'Ambient Meditation Soundscape';
      } else if (backgroundTracks.length > 0) {
        title = 'Relaxing Meditation Background';
      } else if (soundTracks.length > 0) {
        title = 'Nature Sounds Meditation';
      } else {
        title = 'Unguided Meditation Experience';
      }

      // Generate description based on content
      let description = '';
      
      if (frequencies.size > 0) {
        description = `Experience ${Array.from(frequencies).join(', ').toLowerCase()} brainwave entrainment`;
        if (modulations.has('binaural')) {
          description += ' with binaural beats';
        }
        description += ' for deep relaxation and meditation practice.';
      } else if (brainwaveTracks.length > 0) {
        description = `Experience brainwave entrainment`;
        if (modulations.has('binaural')) {
          description += ' with binaural beats';
        }
        description += ' for deep relaxation and meditation practice.';
      } else if (musicTracks.length > 0) {
        description = 'Immerse yourself in calming instrumental music designed for deep meditation and relaxation.';
      } else if (backgroundTracks.length > 0 || soundTracks.length > 0) {
        description = 'Let soothing ambient sounds guide you into a state of deep relaxation and mindfulness.';
      } else {
        description = 'A peaceful unguided meditation experience for deep relaxation and inner focus.';
      }

      // Generate image prompt based on content
      let imagePrompt = '';
      if (frequencies.size > 0) {
        imagePrompt = `${Array.from(frequencies).join(' ').toLowerCase()} brainwave ${Array.from(modulations).join(' ').toLowerCase()} meditation - abstract visualization - no text - no captions`;
      } else if (brainwaveTracks.length > 0) {
        imagePrompt = `brainwave meditation abstract visualization - no text - no captions`;
      } else if (musicTracks.length > 0) {
        imagePrompt = `instrumental meditation music abstract visualization - no text - no captions`;
      } else {
        imagePrompt = `peaceful meditation landscape abstract - no text - no captions`;
      }

      // Generate tags based on content
      const tags: string[] = [];
      
      // Add frequency tags if available
      frequencies.forEach(freq => tags.push(freq));
      
      // Add modulation tags if available
      if (modulations.has('binaural')) tags.push('Binaural Beats');
      if (modulations.has('amplitude')) tags.push('Amplitude Modulation');
      if (modulations.has('frequency')) tags.push('Frequency Modulation');
      if (modulations.has('stereo')) tags.push('Stereo Modulation');
      if (modulations.has('isochronic')) tags.push('Isochronic Tones');
      
      // Add general tags based on track types
      if (musicTracks.length > 0) tags.push('Music');
      if (backgroundTracks.length > 0) tags.push('Relaxing');
      if (soundTracks.length > 0) tags.push('Nature');
      
      // Add default unguided meditation tags
      tags.push('Eyes closed', 'Lying', 'Focus');
      
      // Add additional context tags if no brainwave content
      if (brainwaveTracks.length === 0) {
        if (musicTracks.length > 0) tags.push('Instrumental');
        if (backgroundTracks.length > 0 || soundTracks.length > 0) tags.push('Ambient');
      }

      const metadata = {
        title: title.slice(0, 60), // Ensure reasonable length
        description: description.slice(0, 150), // Ensure reasonable length
        image_prompt: imagePrompt.slice(0, 100), // Ensure reasonable length
        tags: tags.slice(0, 10) // Limit to 10 tags
      };
      
      setScriptMetadata(metadata);
      console.log('Unguided metadata generated successfully:', metadata);
    } catch (error) {
      console.error('Generate unguided metadata error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate unguided metadata';
      setMetadataError(errorMessage);
    } finally {
      setIsGeneratingMetadata(false);
    }
  }, []);

  const generateScriptMetadata = useCallback(async () => {
    if (!scriptData) {
      console.error('Cannot generate metadata: no script data available');
      setMetadataError('No script data available for metadata generation');
      return;
    }

    // Check if script is unguided (instrumental only)
    if (scriptData.script.is_unguided) {
      console.log('Skipping OpenAI metadata generation: script is unguided');
      return;
    }

    try {
      setIsGeneratingMetadata(true);
      setMetadataError(null);
      console.log('Generating script metadata...');

      // Combine all script part texts
      const scriptText = scriptData.parts
        .map(part => part.script_part_text)
        .join('\n\n');

      if (scriptText.trim().length < 50) {
        console.warn('Script text too short for metadata generation');
        setMetadataError('Script text must be at least 50 characters for metadata generation');
        return;
      }

      console.log(`Generating metadata for script text (${scriptText.length} characters)`);
      const metadata = await metadataService.generateMetadata(scriptText);
      
      setScriptMetadata(metadata);
      console.log('Script metadata generated successfully:', metadata);
    } catch (error) {
      console.error('Generate script metadata error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate script metadata';
      setMetadataError(errorMessage);
    } finally {
      setIsGeneratingMetadata(false);
    }
  }, [scriptData]);

  const handleVoiceChange = useCallback((voiceId: string) => {
    // Only reset if the voice is actually changing
    const isVoiceChanging = selectedVoice !== voiceId;
    
    setSelectedVoice(voiceId);
    
    // Find and set the voice data
    const voiceData = availableVoices.find((v: Voice) => v.id === voiceId) || null;
    setSelectedVoiceData(voiceData);
    
    // Update advanced settings with the voice's default speech speed
    if (voiceData) {
      setAdvancedSettings(prev => ({
        ...prev,
        speechSpeed: voiceData.speechSpeed || 1.0
      }));
    }
    
    // Reset all segments' generation status only if voice actually changed
    // This ensures all segments will be regenerated with the new voice
    if (isVoiceChanging && selectedVoice !== 'default') {
      console.log('Voice changed, resetting all segment generation status');
      resetAllSegmentsGenerationStatus();
    }
    
    console.log('Voice changed to:', voiceData?.name || 'unknown');
  }, [availableVoices, setAdvancedSettings, resetAllSegmentsGenerationStatus, selectedVoice]);

  const playSampleAudio = useCallback((voiceId: string) => {
    const voice = availableVoices.find((v: Voice) => v.id === voiceId);
    if (!voice || !voice.sampleAudio) {
      console.error('Voice or sample audio not found:', voiceId);
      return;
    }

    // Create audio element and play sample
    const audio = new Audio(voice.sampleAudio);
    audio.play().catch(error => {
      console.error('Error playing sample audio:', error);
    });
    
    console.log(`Playing sample audio for voice: ${voice.name}`);
  }, [availableVoices]);

  const markContentAsChanged = (partId: string) => {
    setContentData(prev => ({
      ...prev,
      [partId]: (prev[partId] || []).map(segment => ({
        ...segment,
        hasChanged: true
      }))
    }));
  };

  const saveSegmentsAndUpdatePartText = async (partId: string, updatedSegments: TextSegmentData[]) => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    try {
      // Update content data with new segments (use functional update to avoid race conditions)
      setContentData(prev => ({
        ...prev,
        [partId]: updatedSegments
      }));

      // Generate new text representation
      const reconstructedText = segmentsToText(updatedSegments);

      // Update editing content with reconstructed text (use functional update)
      setEditingContent(prev => ({
        ...prev,
        [partId]: reconstructedText
      }));
      
      // If we're editing an existing script and have database IDs, save segments as JSON in part
      if (isEditingExisting && scriptDbId && partDbIds[partId]) {
        try {
          // Transform segments to match backend SegmentData interface
          console.log(`Saving ${updatedSegments.length} segments for part ${partId}:`, updatedSegments.map(s => ({ id: s.id, type: s.type, content: s.content })));
          const segmentsData = {
            segments: updatedSegments.map((segment) => ({
              id: segment.id,
              content: segment.content,
              type: segment.type as 'text' | 'pause',
              // Pause-specific fields
              ...(segment.type === 'pause' && {
                pauseTime: segment.pauseTime || undefined,
                isManuallyModified: segment.isManuallyModified || undefined,
                isParagraphBreak: segment.isParagraphBreak || undefined,
              }),
              // Text-specific fields
              ...(segment.type === 'text' && {
                input_audio_file: segment.inputAudioFile || undefined,
                audio_file: segment.generatedAudioFile || undefined,
                tts_cache_id: segment.ttsCacheId || undefined,
                speech_speed: segment.speechSpeed || undefined,
                // generationStatus not saved - transient UI state only
              })
            })),
            lastParsed: new Date().toISOString()
          };
          
          console.log(`Sending to backend: ${segmentsData.segments.length} segments:`, segmentsData.segments.map(s => ({ id: s.id, type: s.type, content: s.content })));

          // Save to backend - single request updates both text and segments
          const response = await fetch(`${config.apiUrl}/scripts/${scriptDbId}/parts/${partDbIds[partId]}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await authService.getToken()}`
            },
            body: JSON.stringify({
              script_part_text: reconstructedText,
              segments_data: segmentsData,
              part_name: customPartNames[partId]
            })
          });

          if (!response.ok) {
            throw new Error('Failed to update part');
          }

          // Clear the hasChanged flag for all segments after successful save
          setContentData(prev => ({
            ...prev,
            [partId]: (prev[partId] || []).map(seg => ({ ...seg, hasChanged: false, isNew: false }))
          }));

          console.log(`Successfully saved part ${partId} with segments as JSON`);
        } catch (error) {
          console.error('Error updating database:', error);
          // Continue with local updates even if database update fails
        }
      }
      
      console.log(`Segments and part text updated locally for ${partId}`);
      
    } catch (error) {
      console.error('Error saving segments and updating part text:', error);
    }
  };

  // Speech generation functions
  const generateSegmentSpeech = async (partId: string, segmentId: string) => {
    if (!user || !selectedVoiceData) {
      console.error('User not authenticated or no voice selected');
      return;
    }

    const segment = contentData[partId]?.find(s => s.id === segmentId && s.type === 'text');
    if (!segment) {
      console.error('Text segment not found:', { partId, segmentId });
      return;
    }

    try {
      console.log(`Starting speech generation for segment ${segmentId} in part ${partId}`);
      
      // Update segment status to queued
      const updatedSegments = contentData[partId].map(s => 
        s.id === segmentId ? { ...s, generationStatus: 'queued' as const } : s
      );
      setContentData(prev => ({ ...prev, [partId]: updatedSegments }));
      
      // Add to generation queue
      setGenerationQueue(prev => [...prev, `${partId}:${segmentId}`]);
      setIsGenerating(true);

      // Use speech speed from advanced settings
      const speedToUse = segment.speechSpeed || advancedSettings.speechSpeed;
      
      // Prepare request data
      const requestData = {
        segmentId: segmentId,
        scriptId: scriptDbId,
        partId: partDbIds[partId],
        text: segment.content,
        voiceId: selectedVoiceData.voiceId,
        speed: speedToUse,
        audioFileUrl: segment.inputAudioFile || undefined // For speech-to-speech if input audio exists
      };

      console.log('TTS request data:', requestData);

      // Update status to generating
      const generatingSegments = contentData[partId].map(s => 
        s.id === segmentId ? { ...s, generationStatus: 'generating' as const } : s
      );
      setContentData(prev => ({ ...prev, [partId]: generatingSegments }));

      // Call backend API
      const { authService } = await import('../services/auth');
      const { config } = await import('../config');
      const token = authService.getToken();
      
      const response = await fetch(`${config.apiUrl}/generate-segment-speech`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log('TTS generation result:', result);

      // Update segment with generated audio
      const completedSegments = contentData[partId].map(s => 
        s.id === segmentId ? {
          ...s,
          generationStatus: 'completed' as const,
          generatedAudioFile: result.audioFile,
          generationError: null
        } : s
      );
      
      // Update content data directly without triggering full save
      setContentData(prev => ({ ...prev, [partId]: completedSegments }));
      
      // Mark segment as changed - will be saved when saveSegmentsAndUpdatePartText is called
      console.log('Segment audio file updated, will be saved with part');

    } catch (error) {
      console.error('Error generating speech for segment:', error);
      
      // Update segment status to error
      const errorSegments = contentData[partId].map(s => 
        s.id === segmentId ? {
          ...s,
          generationStatus: 'error' as const,
          generationError: error instanceof Error ? error.message : 'Generation failed'
        } : s
      );
      setContentData(prev => ({ ...prev, [partId]: errorSegments }));
      
    } finally {
      // Remove from queue
      setGenerationQueue(prev => prev.filter(item => item !== `${partId}:${segmentId}`));
      
      // Check if any items left in queue
      setGenerationQueue(prev => {
        if (prev.length === 0) {
          setIsGenerating(false);
        }
        return prev;
      });
    }
  };

  const generatePartSpeech = async (partId: string) => {
    if (!user || !selectedVoiceData) {
      console.error('User not authenticated or no voice selected');
      return;
    }

    // Filter segments that need generation (text segments without completed generation)
    const segments = contentData[partId]?.filter(s => 
      s.type === 'text' && 
      s.generationStatus !== 'completed' &&
      s.generationStatus !== 'generating' &&
      s.generationStatus !== 'queued'
    ) || [];
    
    if (segments.length === 0) {
      console.log('No text segments to generate for part:', partId);
      return;
    }

    console.log(`Starting speech generation for ${segments.length} segments in part ${partId}`);
    
    // Generate speech for each text segment sequentially
    for (const segment of segments) {
      await generateSegmentSpeech(partId, segment.id);
    }
  };

  // Track completed segments to prevent duplicates
  const completedSegmentsRef = useRef<Set<string>>(new Set());

  const markSegmentCompleted = useCallback((partId: string, segmentId: string, audioFile: string, ttsCacheId?: string) => {
    const completionKey = `${partId}:${segmentId}:${audioFile}`;
    
    // Prevent duplicate calls for the same segment
    if (completedSegmentsRef.current.has(completionKey)) {
      console.log(`Context: Skipping duplicate completion for ${segmentId}`);
      return;
    }
    
    completedSegmentsRef.current.add(completionKey);
    console.log(`Context: Marking segment ${segmentId} in part ${partId} as completed with audio:`, audioFile, 'ttsCacheId:', ttsCacheId);
    
    setContentData(prevData => {
      const updatedData = { ...prevData };
      // Debug logging (can be removed in production)
      // console.log(`Context: Available parts in contentData:`, Object.keys(updatedData));
      // console.log(`Context: partDbIds mapping:`, partDbIds);
      
      // Try to find the frontend part ID by looking up the database part ID
      let frontendPartId = partId;
      
      // If the partId looks like a database ID (UUID), find the frontend part ID
      if (partId.includes('-') && partId.length > 20) {
        // Find the frontend part ID that maps to this database part ID
        const foundEntry = Object.entries(partDbIds).find(([, dbId]) => dbId === partId);
        if (foundEntry) {
          frontendPartId = foundEntry[0];
          console.log(`Context: Mapped database part ID ${partId} to frontend part ID ${frontendPartId}`);
        } else {
          console.error(`Context: Could not find frontend part ID for database part ID ${partId}`);
          return updatedData; // Return unchanged if we can't find the mapping
        }
      }
      
      const segments = updatedData[frontendPartId];
      
      if (segments) {
        const segmentIndex = segments.findIndex(s => s.id === segmentId);
        
        if (segmentIndex !== -1) {
          updatedData[frontendPartId] = segments.map((seg, idx) => 
            idx === segmentIndex ? {
              ...seg,
              generatedAudioFile: audioFile,
              ttsCacheId: ttsCacheId || seg.ttsCacheId, // Preserve existing ttsCacheId if new one not provided
              generationStatus: 'completed' as const,
              generationError: null,
              hasChanged: false
            } : seg
          );
          console.log(`Context: Updated segment ${segmentId} in frontend part ${frontendPartId} status to completed`);
        } else {
          console.error(`Context: Segment ${segmentId} not found in part ${frontendPartId}`);
        }
      } else {
        console.error(`Context: Frontend part ${frontendPartId} not found in contentData`);
      }
      
      return updatedData;
    });
  }, [partDbIds]);

  const markSegmentError = useCallback((partId: string, segmentId: string, error: string) => {
    console.log(`Context: Marking segment ${segmentId} in part ${partId} as error:`, error);
    
    setContentData(prevData => {
      const updatedData = { ...prevData };
      
      // Try to find the frontend part ID by looking up the database part ID
      let frontendPartId = partId;
      
      // If the partId looks like a database ID (UUID), find the frontend part ID
      if (partId.includes('-') && partId.length > 20) {
        const foundEntry = Object.entries(partDbIds).find(([, dbId]) => dbId === partId);
        if (foundEntry) {
          frontendPartId = foundEntry[0];
          console.log(`Context: Mapped database part ID ${partId} to frontend part ID ${frontendPartId}`);
        } else {
          console.error(`Context: Could not find frontend part ID for database part ID ${partId}`);
          return updatedData;
        }
      }
      
      const segments = updatedData[frontendPartId];
      
      if (segments) {
        const segmentIndex = segments.findIndex(s => s.id === segmentId);
        
        if (segmentIndex !== -1) {
          updatedData[frontendPartId] = segments.map((seg, idx) => 
            idx === segmentIndex ? {
              ...seg,
              generationStatus: 'error' as const,
              generationError: error
            } : seg
          );
          console.log(`Context: Updated segment ${segmentId} in frontend part ${frontendPartId} status to error`);
        }
      }
      
      return updatedData;
    });
  }, [partDbIds]);

  const value: ScriptCreationContextType = {
    // State
    scriptId,
    scriptName,
    isEditingExisting,
    selectedParts,
    customPartNames,
    collapsedParts,
    contentData,
    editingContent,
    selectedVoice,
    selectedLanguage,
    segmentationMode,
    advancedSettings,
    openMenuSegmentId,
    dragState,
    isScriptSaved,
    draftCount,
    availableVoices,
    selectedVoiceData,
    
    // Workflow control
    hasAccessedSpeech,
    isWriteReadOnly,
    shouldOpenToSpeech,
    shouldOpenToAudio,
    shouldOpenToPublish,

    // Intelligent Zen Mode
    zenSettings,
    shouldOpenToZenOptions,
    shouldOpenToZenPreview,
    shouldOpenToZenSummary,

    // Database tracking
    scriptDbId,
    partDbIds,
    segmentDbIds,
    lastSavedContent,
    
    // Speech generation state
    generationQueue,
    isGenerating,
    
    // Audio playback state
    isPlayingAudio,
    currentPlayingPart,
    currentPlayingSegment,
    
    // Audio finalization state
    isFinalizingAudio,
    finalizedAudioFile,
    finalizationError,
    scriptAudioFile,
    scriptFinalAudioFile,
    scriptArtFile,
    
    // Publish form data
    publishTitle,
    publishDescription,
    publishImagePrompt,
    publishTags,
    isPrivate,
    autoLoop,
    isAnonymous,
    isProtected,
    
    // Audio mix state
    audioMixData,
    isAudioMixSaved,
    audioMixError,
    
    // Script metadata state
    scriptMetadata,
    isGeneratingMetadata,
    metadataError,
    scriptData,
    
    // Setters
    setScriptName,
    setSelectedParts,
    setCustomPartNames,
    setCollapsedParts,
    setContentData,
    setEditingContent,
    setAvailableVoices,
    setSelectedVoice,
    setSelectedLanguage,
    setSegmentationMode,
    setAdvancedSettings,
    setOpenMenuSegmentId,
    setDragState,
    setIsScriptSaved,
    setScriptDbId,
    setPartDbIds,
    setSegmentDbIds,
    setLastSavedContent,
    setHasAccessedSpeech,
    setIsWriteReadOnly,
    setShouldOpenToSpeech,
    setShouldOpenToAudio,
    setShouldOpenToPublish,
    setZenSettings,
    setShouldOpenToZenOptions,
    setShouldOpenToZenPreview,
    setShouldOpenToZenSummary,
    setGenerationQueue,
    setIsGenerating,
    setScriptAudioFile,
    setScriptFinalAudioFile,
    setScriptArtFile,
    setPublishTitle,
    setPublishDescription,
    setPublishImagePrompt,
    setPublishTags,
    setIsPrivate,
    setAutoLoop,
    setIsAnonymous,
    setIsProtected,
    setAudioMixData,
    setIsAudioMixSaved,
    setAudioMixError,
    setScriptMetadata,
    setIsGeneratingMetadata,
    setMetadataError,
    setScriptData,
    
    // Actions
    saveScript,
    savePublishData,
    saveSegmentsAndUpdatePartText,
    loadExistingScript,
    loadVoices,
    handleVoiceChange,
    playSampleAudio,
    togglePart,
    togglePartCollapse,
    handlePartNameChange,
    getPartDisplayName,
    movePartUp,
    movePartDown,
    markContentAsChanged,
    generateSegmentSpeech,
    generatePartSpeech,
    markSegmentCompleted,
    markSegmentError,
    resetAllSegmentsGenerationStatus,
    playPartAudio,
    playPartAudioFromSegment,
    stopPartAudio,
    finalizeScriptAudio,
    
    // Audio mix actions
    saveAudioMix,
    loadAudioMix,
    deleteAudioMix,
    setCurrentMixState,
    
    // Metadata actions
    generateScriptMetadata,
    generateUnguidedMetadata,
    
    // Utility functions
    segmentsToText
  };

  return (
    <ScriptCreationContext.Provider value={value}>
      {children}
    </ScriptCreationContext.Provider>
  );
};

export const useScriptCreation = () => {
  const context = useContext(ScriptCreationContext);
  if (context === undefined) {
    throw new Error('useScriptCreation must be used within a ScriptCreationProvider');
  }
  return context;
}; 